package com.islamicmedia.site.pages

import androidx.compose.runtime.*
import com.islamicmedia.shared.AppConfig
import com.islamicmedia.shared.Environment
import com.islamicmedia.shared.presentation.AuthViewModel
import com.islamicmedia.shared.presentation.MediaCheckerViewModel
import com.islamicmedia.shared.presentation.ReferralCopy
import com.islamicmedia.shared.presentation.TaskStatus
import com.islamicmedia.shared.presentation.buildStripEntries
import com.islamicmedia.shared.presentation.viewedTask
import com.islamicmedia.site.components.*
import com.islamicmedia.site.theme.*
import com.varabyte.kobweb.core.Page
import kotlinx.browser.window
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*
import org.w3c.dom.HTMLElement
import org.w3c.dom.events.Event
import org.w3c.dom.url.URLSearchParams
import kotlin.js.json

/**
 * Injects the WiserNotify social-proof pixel ("… just signed up" popups) into the page as a
 * conversion nudge. Fires ONLY on fitrahfilter.com (the production domain — so never on localhost or
 * the dev Fly host) and is called ONLY for visitors without a paid subscription (see the gate in
 * [MainContent]). Idempotent: guarded by `window.t4hto4`, the same flag WiserNotify's own snippet
 * uses to prevent double-installing the pixel.
 */
private fun installSocialProofPixel() {
    if (!window.location.hostname.endsWith("fitrahfilter.com")) return
    val w = window.asDynamic()
    if (w.t4hto4 == true) return
    w.t4hto4 = true
    val doc = kotlinx.browser.document
    val script = doc.createElement("script") as org.w3c.dom.HTMLScriptElement
    script.type = "text/javascript"
    script.async = true
    script.src = "https://pt.wisernotify.com/pixel.js?ti=3sgml1kmnqveye8"
    doc.body?.appendChild(script)
}

/** Reset the window scroll AND any inner overflow container that carries a non-zero scrollTop. */
private fun resetAllScrollPositions() {
    window.scrollTo(0.0, 0.0)
    val all = kotlinx.browser.document.querySelectorAll("*")
    for (i in 0 until all.length) {
        val el = all.item(i) as? org.w3c.dom.HTMLElement ?: continue
        if (el.scrollTop > 0.0) el.scrollTop = 0.0
    }
}

/**
 * A search handed in by URL — `/?q=Moana` runs that search on arrival, exactly as if it had been
 * typed and submitted. This is how another surface (the marketing page, an email, a shared link)
 * starts a search here: an outside page CANNOT drive the search field directly, because Compose
 * owns the query state and ignores synthetic input events, so the URL is the only way in.
 *
 * Held here rather than read where it is used because [Index] returns early behind a spinner while
 * approval is checking, so MainContent mounts more than once. The URL is consumed once, up front,
 * and the value survives those remounts until whichever instance sticks can act on it.
 */
private var pendingLandingQuery: String? = null

/**
 * `/app?auth=1` — open the sign-in form on arrival. This is the landing page's "Log in" button:
 * someone who already has an account should reach it in one click, not have to find the Sign In
 * control after landing. Consumed once, up front, for the same remount reason as
 * [pendingLandingQuery].
 */
private var pendingLandingAuth: Boolean = false

/**
 * Where a browser OAuth round-trip must come back to.
 *
 * Passing this explicitly matters because the alternative is Supabase's own default — its
 * dashboard "Site URL", which is the bare origin. On prod the bare origin is the marketing
 * LANDING PAGE (a static file with no Supabase client), so a Google sign-in that relied on the
 * default came back to a page that could not read the session and silently dropped it: the user
 * appeared to be thrown out to the homepage, not signed in. Naming the return address here means
 * the flow no longer depends on a dashboard setting being right.
 *
 * Returns to the CURRENT page, so signing in never moves you; `/` maps to `/app` because the app
 * only lives at `/` on a local `kobwebStart` (where `/app` works too), never on prod.
 *
 * NOTE: whatever this produces must be on Supabase's allowed-redirect list, same as [Invite]'s.
 */
private fun authReturnUrl(): String {
    val path = window.location.pathname.ifEmpty { "/" }
    return window.location.origin + if (path == "/") "/app" else path
}

/**
 * How far the search bar (with its caption) is pulled up on the home screen when YOUR ANALYSES is
 * showing, so the welcome→bar gap matches the bar→YOUR ANALYSES gap — mirrors mobile's
 * SearchBarLift. Worked out from this page's CSS, not rendered: with analyses the page overflows,
 * the flex spacers sit at their 44px minimum, and welcome→bar is ~83px (8px under the welcome
 * line + 44 spacer + ~16 caption line + 15 caption margin) against the strip's 14px margin.
 * Pulling the bar up 34px makes both ~48px; a spacer under the bar gives the 34px back, so the
 * analyses grid doesn't move.
 */
private const val SEARCH_BAR_LIFT_PX = 34

@Page
@Composable
fun Index() {
    // Apply the dev-only runtime backend override BEFORE any ViewModel / HTTP client is built.
    // The Supabase client and HttpClient read AppConfig URLs at construction time, so this must
    // happen ahead of the first `remember { ... }` call below.
    if (AppConfig.isDev && AppConfig.runtimeOverride == null) {
        val stored = kotlinx.browser.window.localStorage.getItem("env-override")
        AppConfig.runtimeOverride = when (stored) {
            "PROD" -> Environment.PROD
            "DEV" -> Environment.DEV
            else -> null
        }
    }

    // Clean up Supabase auth fragment from URL
    LaunchedEffect(Unit) {
        if (kotlinx.browser.window.location.hash.startsWith("#sb=")) {
            kotlinx.browser.window.history.replaceState(null, "", kotlinx.browser.window.location.pathname)
        }
    }

    // Take ?q= off the URL immediately and hold it (see [pendingLandingQuery]); MainContent runs
    // it once auth settles. Stripping it here means a refresh or a back-navigation won't silently
    // re-run the search, and the shared link never carries the query around after it's been used.
    LaunchedEffect(Unit) {
        val params = URLSearchParams(window.location.search)
        val q = params.get("q")?.trim().orEmpty()
        if (q.isNotEmpty()) pendingLandingQuery = q
        if (params.get("auth") == "1") pendingLandingAuth = true
        // ?launch=1 — arrived from the newsletter's CTA. Read here rather than in a sibling
        // effect: this block strips the params below, so a second effect re-reading the URL
        // would race it.
        val launchApp = params.get("launch") == "1"
        if (q.isNotEmpty() || pendingLandingAuth || launchApp) {
            window.history.replaceState(null, "", window.location.pathname)
        }
        // Hand off to the native app if it's installed. There's no Universal/App Link set up, so
        // this is the same custom-scheme trick auth-callback.html uses. No fallback timer needed:
        // if nothing claims the URL, the user simply stays on the web app they're already on.
        if (launchApp && Regex("Android|iPhone|iPad|iPod", RegexOption.IGNORE_CASE).containsMatchIn(window.navigator.userAgent)) {
            window.location.href = "islamicmedia://open"
        }
    }

    val authViewModel = remember { AuthViewModel() }
    val authState by authViewModel.uiState.collectAsState()

    // Funnel step 1 — the denominator for every drop-off percentage. Once per page load.
    LaunchedEffect(Unit) {
        com.islamicmedia.shared.platform.Funnel.track(
            com.islamicmedia.shared.platform.Event.APP_OPENED,
            com.islamicmedia.shared.platform.FunnelStep.APP_OPENED
        )
    }

    // Show access denied screen if authenticated but not approved (legacy gate; bypassed
    // when payments are enabled — the credit ledger gates access instead).
    if (!AppConfig.PAYMENTS_ENABLED && authState.isAuthenticated && !authState.isCheckingApproval && !authState.isApproved) {
        AccessDeniedScreen(
            userEmail = authState.user?.email,
            onCheckAgain = { authViewModel.recheckApproval() },
            onSignOut = { authViewModel.signOut() }
        )
        return
    }

    // Show loading while checking approval
    if (authState.isCheckingApproval) {
        Div(attrs = {
            style {
                width(100.percent)
                property("min-height", "100dvh")
                display(DisplayStyle.Flex)
                alignItems(AlignItems.Center)
                justifyContent(JustifyContent.Center)
                property("background", GradientHeader)
            }
        }) {
            Div(attrs = {
                style {
                    width(40.px)
                    height(40.px)
                    property("border", "3px solid ${Gray200}")
                    property("border-top-color", Primary.toString())
                    borderRadius(50.percent)
                    property("animation", "spin 0.8s linear infinite")
                }
            })
        }
        InjectSpinKeyframes()
        return
    }

    MainContent(
        isAuthenticated = authState.isAuthenticated,
        isAuthLoading = authState.isLoading,
        userPhotoUrl = authState.user?.photoUrl,
        userEmail = authState.user?.email,
        userDisplayName = authState.user?.displayName,
        authViewModel = authViewModel,
        onSignOut = { authViewModel.signOut() }
    )

    // Post-signup 6-digit OTP modal. Mounted at the page root so it overlays
    // whichever signup path the user took (SignupPrompt / inline registration) —
    // the live web signup forms set awaitingVerification but never rendered it.
    EmailVerificationModal(authViewModel)
}

@Composable
private fun MainContent(
    isAuthenticated: Boolean,
    isAuthLoading: Boolean,
    userPhotoUrl: String?,
    userDisplayName: String? = null,
    userEmail: String?,
    authViewModel: AuthViewModel,
    onSignOut: () -> Unit
) {
    val viewModel = remember { MediaCheckerViewModel() }
    val uiState by viewModel.uiState.collectAsState()
    // Visitor social-proof marquees (founders ticker + reviews) show ONLY to confirmed logged-out
    // visitors — and never while auth is still resolving, so a logged-in user never sees them flash.
    val showVisitorMarquees = !isAuthenticated && !isAuthLoading
    // Founders ticker temporarily hidden (component kept intact) — flip to true to bring it back.
    val showFoundersTicker = false
    // Subscription billing (Stripe credit-ledger). See PRD_SUBSCRIPTION_BILLING.md.
    val billingEntitlement by viewModel.entitlement.collectAsState()
    val billingLoading by viewModel.billingLoading.collectAsState()
    val externalUrl by viewModel.externalUrl.collectAsState()
    LaunchedEffect(externalUrl) {
        externalUrl?.let { url ->
            window.open(url, "_blank")
            viewModel.consumeExternalUrl()
        }
    }
    // Returning from checkout in another tab → refresh balances and proceed if now affordable.
    DisposableEffect(Unit) {
        val onFocus: (org.w3c.dom.events.Event) -> Unit = { viewModel.onAppForegrounded() }
        window.addEventListener("focus", onFocus)
        onDispose { window.removeEventListener("focus", onFocus) }
    }
    LaunchedEffect(Unit) { viewModel.refreshEntitlement() }
    // Collected here, not inside ProfileScreen: they're loaded at app open (from
    // refreshEntitlement() above) so the Account screen is already populated when it opens,
    // instead of growing under the user's cursor as the calls land after the subscreen mounts.
    val billingSummary by viewModel.billingSummary.collectAsState()
    val accountDetailsLoaded by viewModel.accountDetailsLoaded.collectAsState()
    val emailMarketing by viewModel.emailMarketing.collectAsState()

    // Run the deep-linked search (see [pendingLandingQuery]) once things have settled.
    //
    // The delay is the mechanism, not a guess at timing. Index() returns early behind a spinner
    // while approval is checking, so MainContent mounts and is discarded more than once before
    // one sticks. A LaunchedEffect's coroutine is cancelled when its composable leaves, so a
    // doomed instance cancels its own pending dispatch here and never consumes the query —
    // only the instance that survives the wait gets to run the search. Firing immediately
    // started the search on a ViewModel about to be thrown away, killing the request in flight.
    LaunchedEffect(Unit) {
        delay(600)
        val q = pendingLandingQuery ?: return@LaunchedEffect
        pendingLandingQuery = null
        viewModel.onSearchQueryChanged(q)
        viewModel.onSearch(source = "landing")
    }

    // Social-proof popups (WiserNotify) for NON-PAID visitors only, as a conversion nudge.
    // Order of checks matters — the pixel is one-shot (guarded), so we must NOT inject until we're
    // sure the visitor isn't a paying subscriber:
    //   • while auth is still resolving, hold off (isAuthenticated is transiently false at load).
    //   • once resolved & logged out → not a paying subscriber → eligible.
    //   • once resolved & authed → wait for the entitlement, then require no active subscription.
    // The pixel itself only fires on fitrahfilter.com (see installSocialProofPixel).
    // Never while KIDZONE is on or its setup screen is open: a child's screen gets no third-party popups
    // (a lost session with the lock still on would otherwise count as "logged out"). Declared here, above
    // the early returns below, because this effect has to know. If the pixel is already on the page when
    // KIDZONE starts, KidsModeContent hides it.
    val kidsState by viewModel.kids.state.collectAsState()
    var showKidsSetup by remember { mutableStateOf(false) }
    val socialProofEligible = !kidsState.enabled && !showKidsSetup && when {
        isAuthLoading -> false
        !isAuthenticated -> true
        else -> billingEntitlement?.let { !it.isActiveSubscriber } ?: false
    }
    LaunchedEffect(socialProofEligible) {
        if (socialProofEligible) installSocialProofPixel()
    }

    // ---- First-run coachmark tour (native Compose-HTML; replaces Driver.js) ----
    // The "Search anything" tour is retired — spotlighting the search box was the first thing a
    // new visitor met, to explain the one control on screen that needs no explaining. The credits
    // coachmark below survives it. Mirrored on mobile in MainScreen.kt.
    var activeCoach by remember { mutableStateOf<CoachSequence?>(null) }
    val creditsSteps = remember {
        listOf(CoachStep("cm-quickcheck", "Your remaining analyses", "The number on each option is how many of that type you can still run."))
    }
    fun startCredits() {
        if (kotlinx.browser.document.getElementById("cm-quickcheck") != null) {
            viewModel.setCreditsCoachmarkSeen()
            activeCoach = CoachSequence(creditsSteps) { activeCoach = null }
        }
    }
    // The search tour no longer runs, but the flag it used to set still has a job: the credits
    // coachmark below is gated on `getHasSeenCoachmarks()`, so leaving it unset would silently
    // suppress that one too. Mark it seen and chain straight to credits — which is exactly what
    // finishing the tour used to do, minus the spotlight.
    LaunchedEffect(isAuthLoading) {
        if (isAuthLoading || viewModel.getHasSeenCoachmarks()) return@LaunchedEffect
        viewModel.setCoachmarksSeen()
        // Only when logged in — the counts don't exist logged out, and a logged-out visitor
        // picks this up after login via the effect below.
        val badgesReady = !AppConfig.PAYMENTS_ENABLED ||
            billingEntitlement?.quickCount != null || billingEntitlement?.fullCount != null
        if (isAuthenticated && badgesReady && !viewModel.getHasSeenCreditsCoachmark() && activeCoach == null) {
            startCredits()
        }
    }
    // Credits coachmark for returning users who already saw the tour. Login-gated;
    // guarded so it never overlaps the tour.
    LaunchedEffect(isAuthenticated, billingEntitlement) {
        val badgesReady = !AppConfig.PAYMENTS_ENABLED ||
            billingEntitlement?.quickCount != null || billingEntitlement?.fullCount != null
        if (isAuthenticated && badgesReady &&
            viewModel.getHasSeenCoachmarks() && !viewModel.getHasSeenCreditsCoachmark()
        ) {
            kotlinx.coroutines.delay(900)
            if (activeCoach == null) startCredits()
        }
    }
    CoachmarkHost(activeCoach)

    var showHistory by remember { mutableStateOf(false) }
    var showProfile by remember { mutableStateOf(false) }
    var profileStartOnSettings by remember { mutableStateOf(false) }
    var profileStartOnAccount by remember { mutableStateOf(false) }
    /** The avatar menu card (ActiveView.MENU). It replaced a dropdown anchored to the avatar. */
    var showMenu by remember { mutableStateOf(false) }
    // Plan switcher (subscribers tapping their plan pill) + the plan key awaiting confirmation.
    var showPlanSwitcher by remember { mutableStateOf(false) }
    // "Click to get credits" on a canceled membership → subscribe paywall (fresh checkout).
    var showGetCredits by remember { mutableStateOf(false) }
    // Auto-dismiss that modal once the purchase reflects (a live plan exists again).
    LaunchedEffect(billingEntitlement?.status) {
        if (showGetCredits && billingEntitlement?.showNoActiveMembership == false) showGetCredits = false
    }
    var pendingSwitchPlan by remember { mutableStateOf<String?>(null) }
    /** The Help (support/feedback) screen inside the card (ActiveView.HELP), opened from the avatar
     *  menu's "Help". It used to be a centre-screen modal. */
    var showHelp by remember { mutableStateOf(false) }
    var showActivities by remember { mutableStateOf(false) }
    var showAbout by remember { mutableStateOf(false) }
    /** The "Refer a friend" screen inside the card (ActiveView.REFERRAL), opened from the avatar
     *  menu. The menu only offers it when the server says this member can refer (can_refer). */
    var showReferral by remember { mutableStateOf(false) }
    /** The proactive "Did a friend send you a code?" popup (ReferralCopy.REDEEM_PROMPT_*). Local,
     *  not uiState.referralRedeemPromptEligible directly: that flag flips false the instant
     *  onReferralRedeemPromptShown() runs (so it can't fire twice), but the popup itself must stay
     *  on screen until a button is clicked. See the LaunchedEffect below. */
    var showReferralRedeemPrompt by remember { mutableStateOf(false) }
    /** The one-time "your referral was blocked" notice (ReferralCopy.REFERRAL_BLOCKED). Local, for
     *  the same reason as [showReferralRedeemPrompt]: uiState.referralBlockedNoticeEligible only
     *  flips false once OK is clicked (onReferralBlockedNoticeAcknowledged), but this must stay on
     *  screen until then. See the LaunchedEffect below. */
    var showReferralBlockedNotice by remember { mutableStateOf(false) }
    /** Whether the search box itself has focus — reported by SearchWidget/SearchBar. Part of the
     *  referral redeem popup's gate below (it must not appear while the keyboard's up / the
     *  recent-searches dropdown is open). */
    var searchFocused by remember { mutableStateOf(false) }

    /**
     * Clear every full-screen view the avatar menu can open, so the next one set actually wins.
     *
     * [currentView] resolves by PRIORITY, not by most-recently-set (activities > about > profile).
     * So setting `showProfile = true` while `showAbout` is still true resolves to ABOUT and the
     * click looks completely dead — which is exactly what "Settings does nothing from About Us"
     * was. Every menu destination must call this first; leaving it out reintroduces the bug for
     * whichever pair of screens got missed.
     *
     * `showHistory` is deliberately NOT cleared: it sits at the bottom of the priority chain so it
     * can never shadow anything, and closing a view to fall back to History is existing behaviour.
     */
    fun closeMenuViews() {
        showMenu = false
        showHelp = false
        showActivities = false
        showAbout = false
        showProfile = false
        showReferral = false
    }
    val coroutineScope = rememberCoroutineScope()

    // Track if we're showing inline auth form (for unauthenticated users from paywall)
    var showSignupFromPaywall by remember { mutableStateOf(false) }
    var showCancelDialog by remember { mutableStateOf(false) }
    /** When non-null, the user has tapped X on this task's pill and is confirming cancellation. */
    var pendingCancelTaskId by remember { mutableStateOf<String?>(null) }
    /** Id of the task whose ProgressTracker currently has an activity (game/dhikr counter) open.
     *  While this matches the viewed task's id, [currentView] holds PROGRESS even once the
     *  analysis would otherwise flip to RESULTS, so the activity isn't yanked out from under the
     *  user mid-play. Set by [ProgressTracker]'s `onActivityOpenChanged`. */
    var activityOpenTaskId by remember { mutableStateOf<String?>(null) }

    // Initialize subscription service when user authenticates. Keyed on isAuthenticated ONLY —
    // refreshEntitlement() mutates billingEntitlement, so keying this on the entitlement too would
    // re-enter itself forever. Mode pinning is a separate effect below for exactly that reason.
    LaunchedEffect(isAuthenticated) {
        if (isAuthenticated) {
            showSignupFromPaywall = false
            viewModel.onAuthenticated()
            viewModel.refreshEntitlement()
        }
    }

    // Kids Mode: while it's on, the kids screens are the WHOLE page — nothing below this line
    // (header, avatar, menu, paywall, dialogs) is ever composed. After the auth/entitlement
    // effects above on purpose: Kids Mode's eligibility check reads the entitlement.
    LaunchedEffect(kidsState.enabled) { if (kidsState.enabled) showKidsSetup = false }
    // KIDZONE is OFF for every account until the owner switches it on in admin (the entitlement's kids_mode).
    val kidsAllowed = billingEntitlement?.kidsMode == true
    LaunchedEffect(kidsAllowed) { if (!kidsAllowed) showKidsSetup = false }
    if (kidsState.enabled) {
        // Leaving with the PIN goes back to the KIDZONE setup screen (age, Start), not the main page.
        KidsModeContent(viewModel, onExited = { showKidsSetup = true })
        return
    }
    if (showKidsSetup && isAuthenticated && kidsAllowed) {
        KidsSetupContent(
            viewModel = viewModel,
            onClose = { showKidsSetup = false },
            onSeePlans = {
                showKidsSetup = false
                viewModel.showPaywall("kids_mode")
            }
        )
        return
    }

    // Pin the search mode for everyone who can't choose it. Non-subscribers run FULL (their first
    // one is free) — this used to force QUICK, from when the free tier was a free quick check.
    LaunchedEffect(isAuthenticated, billingEntitlement) {
        // `== false` (not `!= true`) on purpose: while the entitlement is still loading it's null,
        // and forcing then would stomp a subscriber's chosen mode on every load.
        if (!isAuthenticated || (AppConfig.PAYMENTS_ENABLED && billingEntitlement?.isUnlimited == false)) {
            viewModel.onQuickCheckChanged(false)
        }
    }

    // Has unlimited access (always true with payments off).
    val isMember = !AppConfig.PAYMENTS_ENABLED || billingEntitlement?.isUnlimited == true
    // Can the user pick quick vs full? Nobody while SEARCH_MODE_CHOICE_ENABLED is off; otherwise
    // subscribers only — mirrors MediaCheckerViewModel.canChooseSearchMode.
    val canChooseSearchMode = AppConfig.SEARCH_MODE_CHOICE_ENABLED && isMember

    // ViewModel requests auth → show inline auth form
    LaunchedEffect(uiState.requiresAuth) {
        if (uiState.requiresAuth) {
            showSignupFromPaywall = true
        }
    }

    // `/app?auth=1` — the landing page's "Log in". Same survive-the-remount wait as the
    // deep-linked search (see [pendingLandingQuery] for why the delay is the mechanism).
    // Skipped when a session already exists: arriving signed in and being handed a sign-in
    // form would be nonsense.
    LaunchedEffect(isAuthenticated) {
        if (!pendingLandingAuth || isAuthenticated) return@LaunchedEffect
        delay(600)
        pendingLandingAuth = false
        showSignupFromPaywall = true
    }

    // Determine which single widget to show
    val viewed = uiState.viewedTask
    // Would this task's analysis show RESULTS right now, absent the activity hold below? Mirrors
    // the two RESULTS conditions just below — kept as one flag so both the override branch and
    // ProgressTracker's `analysisReady` read the exact same thing.
    val viewedWouldShowResults = viewed != null && (
        viewed.status == TaskStatus.COMPLETE ||
            (viewed.status == TaskStatus.RUNNING && viewed.phase1PartialResult != null)
    )
    // An activity (game / dhikr counter) is open for the task currently being viewed — keep
    // PROGRESS on screen even once the analysis is ready, so it isn't yanked away mid-play.
    val activityOpenForViewed = viewed != null && activityOpenTaskId == viewed.id
    val currentView = when {
        showSignupFromPaywall && !isAuthenticated -> ActiveView.SIGNUP_FROM_PAYWALL
        uiState.showPaywall -> ActiveView.PAYWALL
        // Bulk spine scan (library accounts); opened from the avatar menu, closed by closeBulkScan().
        uiState.bulkScan != null && isAuthenticated -> ActiveView.BULK_SCAN
        uiState.viewingHistoryItem != null -> ActiveView.RESULTS
        viewed != null && activityOpenForViewed && viewedWouldShowResults -> ActiveView.PROGRESS
        viewed != null && viewed.status == TaskStatus.COMPLETE -> ActiveView.RESULTS
        // Streamed Phase 1: the verdict has landed and is worth showing while Phase 2 fills in the
        // details. The score it carries is FINAL — capScore reads Phase 1 alone — so this can't be
        // walked back. ERROR/CANCELLED still fall through to PROGRESS, which renders those states.
        viewed != null && viewed.status == TaskStatus.RUNNING && viewed.phase1PartialResult != null ->
            ActiveView.RESULTS
        viewed != null -> ActiveView.PROGRESS  // RUNNING / ERROR / CANCELLED keep progress view
        uiState.showDisambiguation && uiState.disambiguationItems.isNotEmpty() -> ActiveView.DISAMBIGUATION
        uiState.isSearching -> ActiveView.SEARCHING
        showMenu && isAuthenticated -> ActiveView.MENU
        showHelp && isAuthenticated -> ActiveView.HELP
        showReferral && isAuthenticated -> ActiveView.REFERRAL
        showActivities -> ActiveView.ACTIVITIES
        showAbout -> ActiveView.ABOUT
        showProfile && isAuthenticated -> ActiveView.PROFILE
        showHistory && uiState.history.isNotEmpty() -> ActiveView.HISTORY
        else -> ActiveView.SEARCH
    }

    // Drop the activity-open flag whenever it stops applying to the task actually being viewed:
    // the card was closed (closeTask → viewingTaskId null), the analysis was cancelled (the task
    // drops out of activeAnalyses, so `viewed` goes null while viewingTaskId may still be set), or
    // a different task is now being viewed (viewTask → a new id). Covers every exit path so the
    // flag can never get stuck forcing PROGRESS on the wrong (or no) task.
    LaunchedEffect(viewed?.id) {
        if (viewed?.id != activityOpenTaskId) activityOpenTaskId = null
    }

    // Opening a title detail should always start scrolled to the top — otherwise it inherits
    // whatever scrollY the SEARCH view (the "Your Analyses" list) happened to be at. Closing it
    // should restore exactly that saved position rather than snapping back to the top of the list.
    var savedSearchScrollY by remember { mutableStateOf(0.0) }
    var previousViewForScroll by remember { mutableStateOf(currentView) }
    LaunchedEffect(currentView) {
        val previous = previousViewForScroll
        previousViewForScroll = currentView
        if (currentView == ActiveView.RESULTS && previous != ActiveView.RESULTS) {
            savedSearchScrollY = kotlinx.browser.window.scrollY
            resetAllScrollPositions()
            // Compose HTML reuses DOM nodes across view switches (the persistent card's height
            // animation depends on it), so an inner overflow container can carry a stale
            // scrollTop; and that ~410ms height animation reflows the page after we reset.
            // Re-assert on the next frames and once the animation settles.
            delay(50)
            resetAllScrollPositions()
            delay(420)
            resetAllScrollPositions()
        } else if (previous == ActiveView.RESULTS && currentView == ActiveView.SEARCH) {
            // Restoring once isn't enough: the card's shrink animation briefly makes the document
            // shorter than (target + viewport), and while it is the browser clamps scrollY to that
            // smaller maximum and never undoes it — so a lone restore lands at the top. Re-assert
            // until the document can hold the saved offset again. Deliberately settles fast rather
            // than waiting for the layout to fully finish: a longer wait restores more precisely
            // but reads as a visible jump, which is worse than landing a few px off.
            val target = savedSearchScrollY
            kotlinx.browser.window.scrollTo(0.0, target)
            var waited = 0
            while (waited < 700) {
                delay(50)
                waited += 50
                val maxScroll = kotlinx.browser.document.documentElement
                    ?.let { it.scrollHeight - kotlinx.browser.window.innerHeight } ?: 0
                if (maxScroll >= target && kotlin.math.abs(kotlinx.browser.window.scrollY - target) < 1.0) break
                if (maxScroll >= target) kotlinx.browser.window.scrollTo(0.0, target)
            }
        }
    }

    // Auto-reset showHistory/showMenu/showHelp/showProfile/showAbout/showReferral when a higher-priority view takes over
    LaunchedEffect(currentView) {
        if (currentView != ActiveView.HISTORY && currentView != ActiveView.SEARCH && currentView != ActiveView.MENU &&
            currentView != ActiveView.HELP && currentView != ActiveView.PROFILE && currentView != ActiveView.ABOUT &&
            currentView != ActiveView.REFERRAL) {
            showHistory = false
            showMenu = false
            showHelp = false
            showProfile = false
            showAbout = false
            showReferral = false
        }
    }

    // Reload the referral code and counts every time that screen opens: they move as friends pay,
    // and the last answer stays on screen while this runs. Keyed on the view, not showReferral, so
    // coming back to it (e.g. signed in again after a lost session) loads again too. Mirrors mobile.
    val referralOpen = currentView == ActiveView.REFERRAL
    LaunchedEffect(referralOpen) {
        if (referralOpen) viewModel.loadReferral()
    }

    // ── The proactive "Did a friend send you a code?" popup, and the "your referral was blocked"
    // notice ────────────────────────────────────────────────────────────────────────────────────
    // Eligibility (a FRESH loadReferral() answered can_redeem, and this account+backend has never
    // been shown it — see MediaCheckerUiState.referralRedeemPromptEligible) can turn true while
    // some other screen is up; this only decides WHEN it's actually safe to put the popup on top
    // of it — the bare home screen, with nothing else showing and the search box not even
    // focused. If it isn't clear yet this simply waits (same session) rather than interrupting.
    // Mirrors mobile's MainScreen. Shared with the blocked notice below
    // (MediaCheckerUiState.referralBlockedNoticeEligible), which the trailing
    // !showReferralRedeemPrompt / !showReferralBlockedNotice keep mutually exclusive with this one
    // and with itself.
    val referralPromptScreenClear = currentView == ActiveView.SEARCH &&
        !uiState.requiresAuth &&
        uiState.imageCandidates.isEmpty() &&
        !showCancelDialog && pendingCancelTaskId == null &&
        !uiState.showVoucherModal &&
        !uiState.showReferralCodeModal &&
        !uiState.showRedeemCodeModal &&
        !uiState.showSoftCapWarning &&
        uiState.dailyLimitNotice == null &&
        !searchFocused &&
        activeCoach == null &&
        !showReferralRedeemPrompt && !showReferralBlockedNotice
    LaunchedEffect(uiState.referralRedeemPromptEligible, referralPromptScreenClear) {
        if (uiState.referralRedeemPromptEligible && referralPromptScreenClear && !showReferralRedeemPrompt) {
            showReferralRedeemPrompt = true
            // Marks the account seen on the device immediately (before either button is clicked),
            // so a kill mid-popup can never show it twice, and logs REFERRAL_REDEEM_PROMPT_SHOWN.
            viewModel.onReferralRedeemPromptShown()
        }
    }
    LaunchedEffect(uiState.referralBlockedNoticeEligible, referralPromptScreenClear) {
        if (uiState.referralBlockedNoticeEligible && referralPromptScreenClear && !showReferralBlockedNotice) {
            showReferralBlockedNotice = true
        }
    }

    // Browser back button: while a view is layered over the base search screen (an open analysis,
    // paywall, profile, …), back peels off that layer instead of leaving the site — mirroring the
    // mobile PlatformBackHandler. We keep ONE spare history entry while a layer is open: back pops
    // it and the popstate handler closes the layer. Closing via an in-app control (the ✕) removes
    // the spare entry so history stays clean, and back on the base SEARCH view exits as usual.
    val backAction = rememberUpdatedState<() -> Unit> {
        // A Kids Mode check shows what the child saw on the back of its card: Back flips the card
        // back over first and only then leaves the detail (mobile: PlatformBackHandler(enabled = flipped)).
        if (uiState.kidsFlipped) {
            viewModel.closeKidsPreview()
            return@rememberUpdatedState
        }
        when (currentView) {
            ActiveView.RESULTS, ActiveView.DISAMBIGUATION, ActiveView.SEARCHING -> viewModel.onNewSearch()
            ActiveView.PROGRESS -> viewModel.closeTask()
            ActiveView.PAYWALL -> viewModel.onPaywallDismiss()
            ActiveView.SIGNUP_FROM_PAYWALL -> showSignupFromPaywall = false
            ActiveView.MENU -> showMenu = false
            // The screens the menu opens go back UP to it, like their ← (the ✕ goes home). The layer
            // stays open, so the history effect below pushes a fresh spare entry for the next Back.
            ActiveView.PROFILE, ActiveView.ABOUT, ActiveView.ACTIVITIES, ActiveView.HELP, ActiveView.REFERRAL -> {
                closeMenuViews()
                showMenu = true
            }
            ActiveView.BULK_SCAN -> viewModel.closeBulkScan()
            ActiveView.HISTORY -> showHistory = false
            else -> Unit
        }
    }
    val layerOpen = currentView != ActiveView.SEARCH
    val layerOpenState = rememberUpdatedState(layerOpen)
    var pushedForLayer by remember { mutableStateOf(false) }
    // Our own history.back() (below) fires an asynchronous popstate. Without this guard, if the user
    // starts a new search before that popstate lands, onPop sees a layer open again (SEARCHING) and
    // calls backAction → onNewSearch(), which cancels the just-started search and snaps back to a
    // blank search screen. Flag our self-inflicted back() so onPop ignores exactly that one event.
    var ignoreNextPop by remember { mutableStateOf(false) }

    // Keyed on pushedForLayer too: a Back that lands on ANOTHER layer (Account -> Menu) consumes the
    // spare entry but leaves a layer open, so a new one has to be pushed for the Back after it.
    LaunchedEffect(layerOpen, pushedForLayer) {
        // A KIDZONE screen (setup, or Kids Mode after a PIN exit) closed with its spare entry still on top:
        // adopt it as ours, so the branches below keep it for an open layer or pop it if none is open.
        if (KidsHistory.leftSpare) {
            KidsHistory.leftSpare = false
            if (!pushedForLayer) { pushedForLayer = true; return@LaunchedEffect }
        }
        if (layerOpen && !pushedForLayer) {
            window.history.pushState(null, "", window.location.href)
            pushedForLayer = true
        } else if (!layerOpen && pushedForLayer) {
            // Closed by an in-app control (e.g. the ✕) — pop the spare entry we pushed.
            pushedForLayer = false
            ignoreNextPop = true
            window.history.back()
        }
    }

    /* ---- Back, when embedded in the marketing page ----
     * Running inside the landing page's same-origin iframe, Back at the BASE state should return
     * the visitor to that page instead of leaving the site. The catch: a same-origin iframe shares
     * the JOINT session history with its parent, so when the parent pushed its own entry it landed
     * in the middle of the bookkeeping above and desynced it — `history.back()` on a closing layer
     * ate the wrong entry, in-flight searches were cancelled, and selecting a title did nothing.
     *
     * So the entry lives HERE instead, inside the same accounting that already understands it, and
     * the parent never touches history — it is only TOLD when the entry has been consumed. One
     * sentinel, pushed beneath every layer entry, so a layer's own back() always lands on it
     * harmlessly and only a Back at the base state pops it.
     */
    val embedded = remember { runCatching { js("window.parent !== window") as Boolean }.getOrDefault(false) }
    var sentinelArmed by remember { mutableStateOf(false) }
    val sentinelArmedState = rememberUpdatedState(sentinelArmed)
    // NOT armed on load, deliberately: the landing page preloads this app into a hidden iframe
    // long before anyone opens it, and an entry pushed then would swallow the first Back of a
    // visitor who never teleported in at all. The parent arms it at the moment it hands over.

    DisposableEffect(Unit) {
        val onPop: (Event) -> Unit = {
            if (ignoreNextPop) {
                // This popstate is the echo of our own history.back() — swallow it, don't close/cancel.
                ignoreNextPop = false
            } else if (layerOpenState.value) {
                // A real browser Back consumed our spare entry → close the layer instead of leaving the site.
                pushedForLayer = false
                backAction.value()
            } else if (embedded && sentinelArmedState.value) {
                // Base state, embedded, and the sentinel just went: the visitor pressed Back with
                // nothing left to close. Hand it to the landing page to undo its own transition.
                sentinelArmed = false
                runCatching {
                    window.parent.postMessage(
                        json("type" to "ff-exit"),
                        window.location.origin
                    )
                }
            }
        }
        window.addEventListener("popstate", onPop)
        onDispose { window.removeEventListener("popstate", onPop) }
    }

    // The landing page re-arms the sentinel each time it hands off, so a second trip in still has
    // an entry for Back to consume (the first one was spent getting back out).
    DisposableEffect(embedded) {
        val onMsg: (Event) -> Unit = { ev ->
            val me = ev.asDynamic()
            if (me.origin == window.location.origin && me.data != null && me.data.type == "ff-arm") {
                if (embedded && !sentinelArmed) {
                    sentinelArmed = true
                    window.history.pushState(null, "", window.location.href)
                }
            }
        }
        window.addEventListener("message", onMsg)
        onDispose { window.removeEventListener("message", onMsg) }
    }

    // A stopped analysis has nothing left to cancel — auto-dismiss an open
    // "Cancel Analysis?" prompt when the task it refers to stops (completed, errored,
    // cancelled) or is gone. ERROR counts: the dialog offering to cancel a failed task
    // is exactly what trapped users on this screen.
    LaunchedEffect(viewed?.status) {
        if (showCancelDialog && (viewed == null || viewed.status != TaskStatus.RUNNING)) {
            showCancelDialog = false
        }
    }
    LaunchedEffect(pendingCancelTaskId, uiState.activeAnalyses[pendingCancelTaskId]?.status) {
        val id = pendingCancelTaskId ?: return@LaunchedEffect
        val status = uiState.activeAnalyses[id]?.status
        if (status == null || status == TaskStatus.COMPLETE) {
            pendingCancelTaskId = null
        }
    }

    // Height animation for the persistent card
    val cardRef = remember { mutableStateOf<HTMLElement?>(null) }
    val heightRef = remember { object { var value = 0 } }

    LaunchedEffect(currentView) {
        val el = cardRef.value ?: return@LaunchedEffect
        val style = el.style
        val startHeight = heightRef.value
        if (startHeight <= 0) {
            heightRef.value = el.offsetHeight
            return@LaunchedEffect
        }

        // Lock to old height while new content renders
        style.setProperty("height", "${startHeight}px")
        style.setProperty("overflow", "hidden")

        // Wait for DOM to render new content
        delay(30)

        // Measure new natural height
        style.setProperty("height", "auto")
        val endHeight = el.offsetHeight

        if (startHeight == endHeight) {
            style.removeProperty("height")
            style.removeProperty("overflow")
            return@LaunchedEffect
        }

        // Snap back to start height for animation
        style.setProperty("height", "${startHeight}px")
        el.offsetHeight // force reflow

        // Animate to new height
        style.setProperty("transition", "height 0.35s cubic-bezier(0.4, 0, 0.2, 1)")
        style.setProperty("height", "${endHeight}px")

        // Wait for animation to finish
        delay(380)

        // Clean up — return to auto height
        heightRef.value = endHeight
        style.removeProperty("transition")
        style.removeProperty("height")
        style.removeProperty("overflow")
    }

    // Inject global styles
    InjectSpinKeyframes()
    InjectStyles(
        "index-global-styles",
        """
        /* Scroll anchoring would otherwise "helpfully" re-scroll to compensate for the
           persistent card's height-grow animation (see the height-animation effect above),
           silently undoing the explicit top-of-page reset when a detail view opens. */
        html, body {
            overflow-anchor: none;
        }
        @keyframes float {
            0%, 100% { transform: translateY(0px); }
            50% { transform: translateY(-10px); }
        }
        @keyframes fade-in-up {
            from { opacity: 0; transform: translateY(20px); }
            to { opacity: 1; transform: translateY(0); }
        }
        /* The 4px hover lift uses top, NOT transform, and that is load-bearing.
           A transform — even the identity matrix a finished transition leaves behind —
           makes the element a stacking context, which trapped the search bar's
           suggestions panel (z-index: 50) underneath the free-analysis pill and the
           analyses strip. And because you have to hover this card to reach the search
           box, the transform was effectively always present, so the panel was always
           trapped. position: relative + top gives the identical 4px lift and creates
           NO stacking context (relative only does so with a z-index). Don't switch
           this back to translateY. */
        .card-hover {
            position: relative;
            top: 0;
            transition: top 0.25s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.25s cubic-bezier(0.4, 0, 0.2, 1);
        }
        .card-hover:hover {
            top: -4px;
        }
        .close-btn:hover {
            background: rgba(0,0,0,0.08) !important;
        }
        @keyframes results-fade-in {
            from { opacity: 0; transform: translateY(12px); }
            to { opacity: 1; transform: translateY(0); }
        }
        @keyframes card-slide-in {
            from { opacity: 0; transform: translateY(20px); }
            to { opacity: 1; transform: translateY(0); }
        }
        .menu-item:hover {
            background: rgba(0,0,0,0.04);
        }
        /* Shimmer for the DETAILS loading block shown while Phase 2 runs. */
        @keyframes concerns-shimmer {
            0%, 100% { opacity: 0.30; }
            50%      { opacity: 0.62; }
        }
        .concerns-loading-bar {
            background: #1E40AF;
            animation: concerns-shimmer 1.7s ease-in-out infinite;
        }
        /* Hairline between menu rows, INSET from both edges so it reads as a divider between
           items rather than a line cutting the menu in half. A ::before pseudo-element rather
           than border-top, which can't be inset. Mirrors the HorizontalDivider in the mobile
           AnchoredMenu. Applies only inside .menu-list, so it can't leak to other menu-items. */
        .menu-list > .menu-item { position: relative; }
        .menu-list > .menu-item + .menu-item::before {
            content: '';
            position: absolute;
            left: 12px; right: 12px; top: 0;
            height: 1px;
            background: #D4D4D4;
        }
        @keyframes perspective-fade {
            from { opacity: 0; transform: translateY(6px); }
            to { opacity: 1; transform: translateY(0); }
        }
        @media (max-width: 800px) {
            .profile-avatar-header { top: 40px !important; right: 20px !important; }
            /* The menu screens' compact emblem mirrors the avatar, so it moves with it. */
            .compact-header-emblem { top: 40px !important; left: 20px !important; }
        }

        /* ── Animated "northern lights" border around the search card ── */
        @keyframes aurora-glow-yellow {
            0%, 100% {
                box-shadow:
                    0 0 18px 2px rgba(255,210,90,0.55),
                    0 0 40px 6px rgba(255,180,40,0.35),
                    0 0 80px 10px rgba(255,235,140,0.18);
            }
            33% {
                box-shadow:
                    0 0 22px 3px rgba(255,235,140,0.7),
                    0 0 44px 8px rgba(255,200,80,0.45),
                    0 0 80px 10px rgba(255,180,40,0.2);
            }
            66% {
                box-shadow:
                    0 0 26px 4px rgba(255,180,40,0.7),
                    0 0 48px 8px rgba(255,215,100,0.4),
                    0 0 80px 10px rgba(255,200,80,0.2);
            }
        }
        @keyframes aurora-glow-green {
            0%, 100% {
                box-shadow:
                    0 0 18px 2px rgba(90,230,150,0.55),
                    0 0 40px 6px rgba(40,200,110,0.35),
                    0 0 80px 10px rgba(140,255,180,0.18);
            }
            33% {
                box-shadow:
                    0 0 22px 3px rgba(140,255,180,0.7),
                    0 0 44px 8px rgba(80,220,140,0.45),
                    0 0 80px 10px rgba(40,200,110,0.2);
            }
            66% {
                box-shadow:
                    0 0 26px 4px rgba(40,200,110,0.7),
                    0 0 48px 8px rgba(90,230,150,0.4),
                    0 0 80px 10px rgba(80,220,140,0.2);
            }
        }
        .aurora-wrap {
            position: relative;
            animation: aurora-glow-yellow 4s ease-in-out infinite;
        }
        .aurora-wrap.aurora-green {
            animation: aurora-glow-green 4s ease-in-out infinite;
        }

        /* Spinning retry icon while a Phase 2 unit is being re-enriched */
        @keyframes spin-cw {
            from { transform: rotate(0deg); }
            to   { transform: rotate(360deg); }
        }
        .retry-spin {
            display: inline-block;
            animation: spin-cw 0.9s linear infinite;
        }
        """.trimIndent()
    )

    Div(attrs = {
        style {
            property("min-height", "100dvh")   // dvh → fit the visible mobile viewport, no scroll
            property("background", GradientHeader)
            property("background-attachment", "fixed")
            property("background-size", "cover")
            property("overflow-x", "hidden")
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            property("position", "relative")
        }
    }) {
        // Dev-only backend toggle (NEVER rendered on prod builds — guarded by AppConfig.isDev).
        if (AppConfig.isDev) {
            BackendToggleBanner()
        }

        // Founding-members ticker — topmost real element (in prod, where the dev banner is absent).
        // Social proof for visitors only: hidden once the user is logged in.
        if (showVisitorMarquees && showFoundersTicker) {
            FoundingMembersBar()
        }

        // Menu screens (the avatar menu and everything it opens) get the minimized header: the big
        // logo and tagline fold away and a small emblem sits top-left, level with the avatar — the
        // web counterpart of mobile's collapsed header. Home and the search flow keep the full hero.
        val compactHeader = currentView in MenuViews
        // Where the avatar (and so the compact emblem) sits. When the founders ticker is showing
        // (logged-out visitors) it must clear it: dev = below the dev banner + ticker (≈48+42px),
        // prod = below the ticker. Logged-in users have no ticker, so it sits at its normal top.
        val headerControlTop = if (showVisitorMarquees && showFoundersTicker) { if (AppConfig.isDev) 100 else 48 } else 44
        // Height of the collapsed header: down to just under the avatar row (top + 44px + 8px). The
        // avatar is positioned from the page top but this header sits BELOW the dev banner (≈48px),
        // so on dev builds the banner already covers that much of it.
        val compactHeaderHeight = headerControlTop + 44 + 8 - (if (AppConfig.isDev) 48 else 0)

        // Centered logo and tagline header — collapses to compactHeaderHeight on menu screens.
        Div(attrs = {
            style {
                width(100.percent)
                padding(36.px, 20.px, 0.px)
                display(DisplayStyle.Flex)
                flexDirection(FlexDirection.Column)
                alignItems(AlignItems.Center)
                property("text-align", "center")
                property("box-sizing", "border-box")
                // auto <-> px only animates with interpolate-size (Chromium); elsewhere it snaps.
                property("interpolate-size", "allow-keywords")
                property("height", if (compactHeader) "${compactHeaderHeight}px" else "auto")
                property("overflow", "hidden")
                property("transition", "height 0.3s cubic-bezier(0.4, 0, 0.2, 1)")
            }
        }) {
            Img(
                src = "/images/logo.png",
                attrs = {
                    attr("alt", "Fitra Filter")
                    style {
                        width(160.px)
                        height(160.px)
                        property("object-fit", "contain")
                        property("filter", "drop-shadow(0 4px 12px rgba(0,0,0,0.2))")
                        opacity(if (compactHeader) 0 else 1)
                        property("transform", if (compactHeader) "scale(0.6)" else "scale(1)")
                        property("transform-origin", "top center")
                        property("transition", "opacity 0.2s ease, transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)")
                    }
                }
            )
            P(attrs = {
                style {
                    color(Color("#5a6a6a"))
                    fontSize(13.px)
                    margin(6.px, 0.px, 0.px)
                    fontWeight(600)
                    property("letter-spacing", "0.5px")
                    opacity(if (compactHeader) 0 else 1)
                    property("transition", "opacity 0.2s ease")
                }
            }) {
                Text("Protecting Fitrah. Empowering Parents.")
            }
        }

        // The minimized header's emblem, top-left and level with the avatar. Its own image rather
        // than the big logo shrunk: the full logo is mark-above-wordmark, and at 44px the wordmark
        // would be unreadable. Same picture as mobile's collapsed logo (logo_icon).
        Img(
            src = "/images/logo_icon.png",
            attrs = {
                classes("compact-header-emblem")
                attr("alt", "")
                attr("aria-hidden", "true")
                style {
                    property("position", "absolute")
                    property("top", "${headerControlTop}px")
                    property("left", "max(20px, calc(50% - 337.5px + 20px))")
                    width(44.px)
                    height(44.px)
                    property("z-index", "100")
                    property("pointer-events", "none")
                    opacity(if (compactHeader) 1 else 0)
                    property("transform", if (compactHeader) "scale(1)" else "scale(0.6)")
                    property("transform-origin", "top left")
                    property("transition", "opacity 0.25s ease, transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)")
                }
            }
        )

        // Profile avatar in top-right (absolute position). Tapping it opens the menu card (MENU).
        Div(attrs = {
            classes("profile-avatar-header")
            style {
                property("position", "absolute")
                property("top", "${headerControlTop}px")
                property("right", "max(20px, calc(50% - 337.5px + 20px))")
                property("z-index", "100")
            }
        }) {
            if (isAuthenticated || !isAuthLoading) {
                if (isAuthenticated) {
                    // Avatar button (authenticated)
                    Button(attrs = {
                        onClick {
                            when (currentView) {
                                // A second click puts the menu away again, as it did the dropdown.
                                ActiveView.MENU -> showMenu = false
                                else -> {
                                    // MENU ranks below the paywall, an open analysis and the search
                                    // flow (see currentView), so step out of those first — the same
                                    // exits the browser back button uses — or the click would change
                                    // nothing on screen.
                                    when (currentView) {
                                        ActiveView.RESULTS, ActiveView.DISAMBIGUATION,
                                        ActiveView.SEARCHING -> viewModel.onNewSearch()
                                        ActiveView.PROGRESS -> viewModel.closeTask()
                                        ActiveView.PAYWALL -> viewModel.onPaywallDismiss()
                                        else -> {}
                                    }
                                    closeMenuViews()
                                    showMenu = true
                                }
                            }
                        }
                        style {
                            width(44.px)
                            height(44.px)
                            borderRadius(50.percent)
                            property("border", "2px solid #c6a047")
                            padding(0.px)
                            cursor("pointer")
                            property("overflow", "hidden")
                            property("background", "white")
                            property("transition", TransitionMedium)
                        }
                    }) {
                        if (userPhotoUrl != null) {
                            Img(
                                src = userPhotoUrl,
                                attrs = {
                                    addEventListener("error") {
                                        val img = it.target as? org.w3c.dom.HTMLImageElement
                                        img?.src = "/images/default_avatar.png"
                                    }
                                    style {
                                        width(100.percent)
                                        height(100.percent)
                                        property("object-fit", "cover")
                                    }
                                }
                            )
                        } else {
                            // Generic user icon when no profile picture
                            Img(
                                src = "/images/default_avatar.png",
                                attrs = {
                                    style {
                                        width(100.percent)
                                        height(100.percent)
                                        property("object-fit", "cover")
                                    }
                                }
                            )
                        }
                    }
                } else {
                    // Sign In text button (unauthenticated)
                    Button(attrs = {
                        onClick {
                            showSignupFromPaywall = true
                        }
                        style {
                            property("background", "$Primary")
                            color(Color.white)
                            property("border", "none")
                            borderRadius(20.px)
                            padding(10.px, 20.px)
                            fontSize(13.px)
                            fontWeight("600")
                            cursor("pointer")
                            property("transition", TransitionMedium)
                            property("white-space", "nowrap")
                        }
                    }) {
                        Text("Sign In")
                    }
                }

            }
        }

        // Main content
        val topPad = when (currentView) {
            ActiveView.SEARCH -> "0"
            ActiveView.PAYWALL, ActiveView.SIGNUP_FROM_PAYWALL -> "5vh"
            else -> "12px"
        }
        Div(attrs = {
            style {
                property("max-width", "675px")
                property("margin", "0 auto")
                padding(0.px, 20.px)
                width(100.percent)
                property("box-sizing", "border-box")
                property("flex", "1")
                property("min-height", "0")
                display(DisplayStyle.Flex)
                flexDirection(FlexDirection.Column)
                // Menu screens sit centred in the room under the minimized header (mobile does the same).
                justifyContent(if (currentView in MenuViews) JustifyContent.Center else JustifyContent.FlexStart)
                property("padding-top", topPad)
                property("transition", "padding-top 0.4s cubic-bezier(0.4, 0, 0.2, 1)")
                property("position", "relative")
                property("z-index", "1")
            }
        }) {
            // Built up here rather than inline in the strip call because whether there are any
            // entries also decides whether the search bar is lifted (SEARCH_BAR_LIFT_PX).
            val showsStrip = currentView == ActiveView.SEARCH ||
                currentView == ActiveView.SEARCHING ||
                currentView == ActiveView.DISAMBIGUATION
            val stripEntries = if (showsStrip) uiState.buildStripEntries(
                onViewTask = viewModel::viewTask,
                onHistoryClick = viewModel::onHistoryItemClick,
                onCancelRequest = { id -> pendingCancelTaskId = id },
                // Pin/hide persist per-account, so only offer them when signed in.
                onTogglePin = if (isAuthenticated) viewModel::togglePin else null,
                onHide = if (isAuthenticated) viewModel::hideFromList else null,
                // A bulk-scan book still being analysed opens the batch.
                onViewBulkBatch = viewModel::openBulkScan,
            ) else emptyList()
            val liftSearchBar = currentView == ActiveView.SEARCH && stripEntries.isNotEmpty()

            if (currentView == ActiveView.RESULTS) {
                Div(attrs = {
                    style {
                        // `backwards` not `both` — same reason as card-slide-in above: `both` leaves an
                        // identity transform applied, making this a stacking context for no visual gain.
                        property("animation", "results-fade-in 0.35s cubic-bezier(0.4, 0, 0.2, 1) backwards")
                    }
                }) {
                    ResultsWidget(
                        uiState = uiState,
                        viewModel = viewModel,
                        isSignedIn = isAuthenticated,
                        firstName = userDisplayName?.split(" ")?.firstOrNull(),
                    )
                }
            } else {
                // Dynamic vertical centring (viewport-independent): three flex-grow spacers
                // distribute the content column's free space so the welcome text always sits
                // centred between the hero banner and the search-bar cluster, on ANY screen
                // height. spacerTop == spacerMid keeps the welcome centred; the heavier
                // spacerBottom (see end of this column) settles the search bar in the lower third.
                if (currentView == ActiveView.SEARCH) {
                    Div(attrs = { style { property("flex", "1 1 0"); property("min-height", "44px") } })
                }
                if (currentView == ActiveView.SEARCH) {
                    val firstName = userDisplayName?.split(" ")?.firstOrNull()
                    Div(attrs = {
                        style {
                            property("margin", "0")
                            property("text-align", "center")
                            property("font-family", "'Source Serif 4', Georgia, serif")
                            fontSize(24.px)
                            fontWeight(400)
                            color(Color.white)
                            property("text-shadow", "0 0 20px rgba(14, 124, 123, 0.4), 0 0 40px rgba(14, 124, 123, 0.2)")
                        }
                    }) {
                        if (firstName != null) {
                            Text("Welcome, $firstName")
                        } else {
                            Text("Search any media")
                            org.jetbrains.compose.web.dom.Br()
                            Text("We'll analyze it for Islamic")
                            org.jetbrains.compose.web.dom.Br()
                            Text("& developmental concerns")
                        }
                    }
                }

                // spacerMid — equal weight to spacerTop, so the welcome text is centred in the
                // gap between the hero banner and the search cluster below.
                if (currentView == ActiveView.SEARCH) {
                    Div(attrs = { style { property("flex", "1 1 0"); property("min-height", "44px") } })
                }

                // Search description text above the card
                if (currentView == ActiveView.SEARCH) {
                    P(attrs = {
                        style {
                            color(Color("#5a6a6a"))
                            fontSize(13.px)
                            fontWeight(400)
                            property("letter-spacing", "0.5px")
                            property("text-align", "center")
                            margin(0.px, 0.px, 15.px)
                            // Pulls the caption + bar up into the spacer above; given back below the bar.
                            if (liftSearchBar) property("margin-top", "-${SEARCH_BAR_LIFT_PX}px")
                        }
                    }) {
                        Text("Search any book, movie, TV show, video game, or YouTube channel")
                    }
                }

                // Single persistent card — interpolate-size smoothly
                // animates height when content changes between states
                Div(attrs = {
                    style {
                        property("position", "relative")
                        width(100.percent)
                    }
                }) {
                    val showAurora = currentView == ActiveView.SEARCH

                    // Subtle 2-state breathing pulse driven from Kotlin. CSS transition smooths the
                    // shift between the two states so the card appears to gently breathe.
                    var pulseHigh by remember { mutableStateOf(false) }
                    LaunchedEffect(showAurora) {
                        if (!showAurora) return@LaunchedEffect
                        while (true) {
                            kotlinx.coroutines.delay(1800)
                            pulseHigh = !pulseHigh
                        }
                    }

                    Div(attrs = {
                        classes("card-hover")
                        ref { element ->
                            cardRef.value = element.unsafeCast<HTMLElement>()
                            onDispose { cardRef.value = null }
                        }
                        style {
                            property("background", GradientCard)
                            borderRadius(RadiusXLarge.unsafeCast<CSSNumeric>())
                            if (showAurora) {
                                // Two-layer breathing aurora glow — vibrant primary ring + softer halo.
                                // Yellow matches the #FACC15 Quick check button bg; teal matches Primary.
                                // CSS transition smoothly interpolates BOTH the breathing pulse AND the
                                // color swap when the user toggles Quick ↔ Full, giving a slide-and-fade
                                // feel between the two modes.
                                val opacity = if (pulseHigh) 0.85 else 0.55
                                val blur = if (pulseHigh) 16 else 10
                                val spread = if (pulseHigh) 2 else 0
                                val haloOpacity = opacity * 0.55
                                val color = if (uiState.quickCheck)
                                    "rgba(250,204,21,$opacity)"   // #FACC15 vivid yellow-400
                                else
                                    "rgba(14,124,123,$opacity)"   // Primary teal
                                val haloColor = if (uiState.quickCheck)
                                    "rgba(253,224,71,$haloOpacity)"  // #FDE047 lighter yellow halo
                                else
                                    "rgba(20,184,166,$haloOpacity)"  // teal-400 halo
                                property("box-shadow",
                                    "0 0 ${blur}px ${spread}px $color, 0 0 ${blur * 2}px ${spread + 4}px $haloColor")
                                // `top` must be listed too: this inline transition overrides the one
                                // on .card-hover, so without it the class's hover lift snaps.
                                // (Was `transform` — the lift moved to top/position:relative because
                                // any transform makes this a stacking context and traps the search
                                // suggestions panel underneath the pill and the analyses strip.)
                                property("transition", "box-shadow 0.8s ease-in-out, top 0.25s cubic-bezier(0.4, 0, 0.2, 1)")
                            } else {
                                property("box-shadow", NeuBoxShadow)
                            }
                        }
                    }) {
                        key(currentView) {
                        Div(attrs = {
                            style {
                                // `backwards`, NOT `both`. `both` = backwards + forwards, and the
                                // forwards half holds the `to` keyframe forever — leaving
                                // transform: matrix(1,0,0,1,0,0) permanently applied. That identity
                                // transform does NOTHING visually but DOES make this element a
                                // stacking context, which trapped the search bar's suggestions panel
                                // (z-index:50) below the free-analysis pill and the analyses strip,
                                // and made position:fixed overlays centre on this box instead of the
                                // viewport. `to` here is identical to the element's natural state, so
                                // dropping forwards is visually identical and leaves transform: none.
                                // Do NOT "fix" descendant stacking with z-index — verified on prod
                                // that raising this wrapper only reshuffles within it and makes the
                                // strip paint through the recent-searches panel.
                                property("animation", "card-slide-in 0.3s cubic-bezier(0.4, 0, 0.2, 1) backwards")
                            }
                        }) {
                        when (currentView) {
                            ActiveView.SEARCH -> {
                                SearchWidget(
                                    uiState = uiState,
                                    viewModel = viewModel,
                                    userDisplayName = userDisplayName,
                                    isAuthenticated = isAuthenticated,
                                    onFocusChanged = { searchFocused = it }
                                )
                            }

                            ActiveView.SEARCHING -> {
                                Div(attrs = {
                                    style {
                                        display(DisplayStyle.Flex)
                                        flexDirection(FlexDirection.Column)
                                        alignItems(AlignItems.Center)
                                        justifyContent(JustifyContent.Center)
                                        padding(40.px)
                                    }
                                }) {
                                    // Text first, animation beneath it — the status reads before
                                    // the spinner rather than under it.
                                    Div(attrs = {
                                        style {
                                            fontSize(18.px)
                                            fontWeight(600)
                                            color(Primary)
                                        }
                                    }) { Text("Finding matches...") }
                                    Div(attrs = {
                                        style {
                                            marginTop(8.px)
                                            fontSize(14.px)
                                            color(Gray500)
                                        }
                                    }) { Text("Searching for \"${uiState.searchQuery}\"") }
                                    Div(attrs = { style { marginTop(20.px) } }) {
                                        SearchingAnimation(size = 140)
                                    }
                                }
                            }

                            ActiveView.DISAMBIGUATION -> {
                                DisambiguationList(
                                    items = uiState.disambiguationItems,
                                    onItemSelected = viewModel::onDisambiguationItemSelected
                                )
                            }

                            ActiveView.PROGRESS -> {
                                val task = viewed
                                if (task == null || task.progressSteps.isEmpty()) {
                                    Div(attrs = {
                                        style {
                                            display(DisplayStyle.Flex)
                                            justifyContent(JustifyContent.Center)
                                            padding(40.px)
                                            color(Gray500)
                                            fontSize(16.px)
                                        }
                                    }) { Text("Initializing...") }
                                } else {
                                    ProgressTracker(
                                        steps = task.progressSteps,
                                        progress = task.progressFraction,
                                        currentCost = task.costUSD,
                                        title = task.displayTitle,
                                        author = task.result?.author ?: task.item.author,
                                        mediaType = task.mediaType,
                                        quickCheck = task.quickCheck,
                                        cacheHit = task.wasCacheHit,
                                        coverUrl = task.coverUrl,
                                        analysisStartedAtMs = if (task.status == TaskStatus.RUNNING) task.startedAtMs else null,
                                        onSearchMore = if (task.status != TaskStatus.COMPLETE) {
                                            { viewModel.closeTask() }
                                        } else null,
                                        analysisReady = viewedWouldShowResults,
                                        onViewAnalysis = { activityOpenTaskId = null },
                                        onActivityOpenChanged = { open ->
                                            activityOpenTaskId = if (open) task.id else null
                                        }
                                    )
                                }
                            }

                            ActiveView.HISTORY -> {
                                HistoryContent(
                                    history = uiState.history,
                                    onItemClick = viewModel::onHistoryItemClick,
                                    layout = uiState.displaySettings.analysesLayout
                                )
                            }

                            ActiveView.MENU -> {
                                // The avatar's menu, as a card. Every destination calls
                                // closeMenuViews() first: currentView resolves by priority, so a
                                // stale flag would otherwise win over the one just set.
                                com.islamicmedia.site.components.MenuScreen(
                                    items = buildList {
                                        add(com.islamicmedia.site.components.MenuItem("Profile") {
                                            closeMenuViews()
                                            profileStartOnSettings = false
                                            profileStartOnAccount = false
                                            showProfile = true
                                        })
                                        add(com.islamicmedia.site.components.MenuItem("Account") {
                                            closeMenuViews()
                                            profileStartOnSettings = false
                                            profileStartOnAccount = true
                                            showProfile = true
                                        })
                                        add(com.islamicmedia.site.components.MenuItem("Settings") {
                                            closeMenuViews()
                                            profileStartOnSettings = true
                                            profileStartOnAccount = false
                                            showProfile = true
                                        })
                                        // Only for an account the owner switched KIDZONE on for in admin
                                        // (the entitlement's kids_mode). Hidden, not disabled, for everyone
                                        // else. Mirrors mobile.
                                        if (billingEntitlement?.kidsMode == true) {
                                            add(com.islamicmedia.site.components.MenuItem("KIDZONE", content = { com.islamicmedia.site.components.KidzoneMenuLabel() }) {
                                                showMenu = false
                                                showKidsSetup = true
                                            })
                                        }
                                        // Library accounts only (they hold a search pack). Mirrors mobile.
                                        if (viewModel.bulkScanAvailable) {
                                            add(com.islamicmedia.site.components.MenuItem("Scan a stack of books") {
                                                showMenu = false
                                                viewModel.openBulkScan()
                                            })
                                        }
                                        // Only for members the server says can refer (can_refer):
                                        // hidden, not disabled, for everyone else. Mirrors mobile.
                                        if (billingEntitlement?.canRefer == true) {
                                            add(com.islamicmedia.site.components.MenuItem(ReferralCopy.MENU_ITEM) {
                                                closeMenuViews()
                                                showReferral = true
                                            })
                                        }
                                        add(com.islamicmedia.site.components.MenuItem("About Us") {
                                            closeMenuViews()
                                            showAbout = true
                                        })
                                        add(com.islamicmedia.site.components.MenuItem("Help") {
                                            closeMenuViews()
                                            showHelp = true
                                        })
                                        add(com.islamicmedia.site.components.MenuItem("Sign Out") {
                                            showMenu = false
                                            viewModel.onSignedOut()
                                            onSignOut()
                                        })
                                    },
                                )
                            }

                            ActiveView.PROFILE -> {
                                ProfileScreen(
                                    userEmail = userEmail,
                                    userPhotoUrl = userPhotoUrl,
                                    userName = userDisplayName,
                                    isSubscribed = billingEntitlement?.isActiveSubscriber ?: false,
                                    entitlement = billingEntitlement,
                                    history = uiState.history,
                                    onManageSubscription = { viewModel.openBillingPortal() },
                                    billingSummary = billingSummary,
                                    billingLoaded = accountDetailsLoaded,
                                    emailMarketing = emailMarketing,
                                    onEmailMarketingChanged = { viewModel.setEmailMarketing(it) },
                                    onRefreshAccountDetails = { viewModel.refreshAccountDetails() },
                                    onEntitlementChanged = { viewModel.refreshEntitlement() },
                                    onResumeSubscription = { viewModel.resumeSubscription() },
                                    onDeleteAccount = { authViewModel.deleteAccount() },
                                    onHistoryItemClick = { item ->
                                        showProfile = false
                                        viewModel.onHistoryItemClick(item)
                                    },
                                    billingLoading = billingLoading,
                                    onPlanClick = { showPlanSwitcher = true },
                                    onGetCredits = { showGetCredits = true },
                                    plans = uiState.pricingPlans,
                                    displaySettings = uiState.displaySettings,
                                    onDisplaySettingsChanged = { viewModel.onDisplaySettingsChanged(it) },
                                    startOnSettings = profileStartOnSettings,
                                    startOnAccount = profileStartOnAccount
                                )
                            }

                            ActiveView.ACTIVITIES -> {
                                com.islamicmedia.site.components.ActivitiesScreen()
                            }

                            ActiveView.ABOUT -> {
                                com.islamicmedia.site.components.AboutScreen(viewModel = viewModel)
                            }

                            ActiveView.BULK_SCAN -> {
                                uiState.bulkScan?.let { com.islamicmedia.site.components.BulkScanScreen(viewModel, it) }
                            }

                            ActiveView.REFERRAL -> {
                                ReferralScreen(
                                    info = uiState.referralInfo,
                                    loading = uiState.referralLoading,
                                    error = uiState.referralError,
                                    onRetry = viewModel::loadReferral,
                                    onCopied = viewModel::onReferralCodeCopied,
                                    onShared = viewModel::onReferralShared,
                                    onOpenRedeem = viewModel::onOpenRedeemCodeModal,
                                )
                            }

                            ActiveView.HELP -> {
                                com.islamicmedia.site.components.FeedbackForm(
                                    // "Close" on the thank-you state goes back to the menu, where the
                                    // old modal used to leave the user.
                                    onDone = {
                                        closeMenuViews()
                                        showMenu = true
                                    },
                                    onSubmit = { message, imageBytes, imageName ->
                                        viewModel.submitFeedback(message, imageBytes, imageName, userEmail, userDisplayName)
                                    },
                                    initialName = userDisplayName,
                                    initialEmail = userEmail,
                                )
                            }

                            // PAYWALL renders at the TOP LEVEL of this page, not here — see the
                            // hoisted block near the voucher modal. Its backdrop is position:fixed,
                            // and the card-slide-in wrapper above holds a permanent transform, which
                            // would make it position against that wrapper's box instead of the
                            // viewport — the modal ended up pinned near the top of the page rather
                            // than centred on screen.
                            ActiveView.PAYWALL -> {}

                            ActiveView.SIGNUP_FROM_PAYWALL -> {
                                SignupPrompt(
                                    authViewModel = authViewModel,
                                    selectedTitle = uiState.selectedTitle ?: uiState.searchQuery,
                                    onClose = {
                                        showSignupFromPaywall = false
                                        viewModel.onPaywallDismiss()
                                    }
                                )
                            }

                            else -> {}
                        }
                        }
                        }
                    }

                    // Close button (non-SEARCH states). PAYWALL is excluded — it renders as a
                    // full-screen blocking modal with its own dismiss, so no card-corner X.
                    if (currentView != ActiveView.SEARCH && currentView != ActiveView.PAYWALL) {
                        Button(attrs = {
                            classes("close-btn")
                            onClick {
                                when (currentView) {
                                    ActiveView.SEARCHING -> viewModel.onNewSearch()
                                    ActiveView.DISAMBIGUATION -> viewModel.onNewSearch()
                                    // Only offer to CANCEL something still running. An ERROR'd
                                    // task is pinned to this view by `viewed != null`, so offering
                                    // "Cancel Analysis? / Complete Analysis" for an already-stopped
                                    // task left no route back to the main screen.
                                    ActiveView.PROGRESS -> {
                                        if (viewed?.status == TaskStatus.RUNNING) {
                                            showCancelDialog = true
                                        } else {
                                            activityOpenTaskId = null
                                            viewModel.closeTask()
                                        }
                                    }
                                    ActiveView.HISTORY -> { showHistory = false }
                                    ActiveView.MENU -> { showMenu = false }
                                    ActiveView.HELP -> { showHelp = false }
                                    ActiveView.PROFILE -> { showProfile = false }
                                    ActiveView.ACTIVITIES -> { showActivities = false }
                                    ActiveView.ABOUT -> { showAbout = false }
                                    ActiveView.REFERRAL -> { showReferral = false }
                                    ActiveView.BULK_SCAN -> viewModel.closeBulkScan()
                                    ActiveView.PAYWALL -> {
                                        viewModel.onPaywallDismiss()
                                    }
                                    ActiveView.SIGNUP_FROM_PAYWALL -> {
                                        showSignupFromPaywall = false
                                        viewModel.onPaywallDismiss()
                                    }
                                    else -> {}
                                }
                            }
                            style {
                                property("position", "absolute")
                                property("top", "8px")
                                property("right", "8px")
                                width(36.px)
                                height(36.px)
                                borderRadius(50.percent)
                                property("background", "transparent")
                                property("border", "none")
                                cursor("pointer")
                                display(DisplayStyle.Flex)
                                alignItems(AlignItems.Center)
                                justifyContent(JustifyContent.Center)
                                fontSize(20.px)
                                color(Gray400)
                                property("transition", "all 0.2s ease")
                                property("z-index", "10")
                            }
                        }) { Text("\u2715") }
                    }

                    // ← back up to the Menu card from the screens it opens (Profile, Account,
                    // Settings, Refer a friend, About Us, Help). Mirrors the ✕ opposite, which still goes home.
                    if (currentView in MenuViews && currentView != ActiveView.MENU && currentView != ActiveView.BULK_SCAN) {
                        Button(attrs = {
                            classes("close-btn")
                            attr("aria-label", "Back to menu")
                            onClick {
                                closeMenuViews()
                                showMenu = true
                            }
                            style {
                                property("position", "absolute")
                                property("top", "8px")
                                property("left", "8px")
                                width(36.px)
                                height(36.px)
                                borderRadius(50.percent)
                                property("background", "transparent")
                                property("border", "none")
                                cursor("pointer")
                                display(DisplayStyle.Flex)
                                alignItems(AlignItems.Center)
                                justifyContent(JustifyContent.Center)
                                fontSize(20.px)
                                color(Gray400)
                                property("transition", "all 0.2s ease")
                                property("z-index", "10")
                            }
                        }) { Text("\u2190") }
                    }
                }

                // Error report modal
                if (uiState.errorMessage != null) {
                    ErrorReportModal(
                        message = uiState.errorMessage ?: "",
                        onDismiss = { viewModel.onDismissError() },
                        onSubmit = { userMessage -> viewModel.submitErrorReport(userMessage) }
                    )
                }

            }

                // Gives back the height the caption's negative margin took (SEARCH_BAR_LIFT_PX), so
                // the bar moves up while the analyses grid stays put. flex-shrink 0: an empty flex
                // child would otherwise shrink to nothing once the page overflows.
                if (liftSearchBar) {
                    Div(attrs = { style { height(SEARCH_BAR_LIFT_PX.px); property("flex-shrink", "0") } })
                }

                // Quick check / Full analysis toggle — subscribers only. Everyone else is pinned to
                // FULL, so there's nothing to toggle; they get the nudge below instead.
                if (currentView == ActiveView.SEARCH && isAuthenticated && canChooseSearchMode) {
                    QuickCheckToggle(
                        quickCheck = uiState.quickCheck,
                        onChange = viewModel::onQuickCheckChanged,
                        disabled = uiState.isSearching,
                        quickCount = if (AppConfig.PAYMENTS_ENABLED) billingEntitlement?.quickCount else null,
                        fullCount = if (AppConfig.PAYMENTS_ENABLED) billingEntitlement?.fullCount else null
                    )
                }

                // "Your first analysis is free" — shown in the toggle's place, to set the
                // expectation BEFORE the analysis is spent so the paywall on search #2 isn't a shock.
                //
                // Shown to signed-OUT visitors too, deliberately. The main path is search → pick a
                // title → register → the analysis starts immediately (onAuthenticated replays the
                // pending item), so a user who signs up through the gate never sits on the search
                // screen while authenticated with the free analysis still unspent. Gating this on
                // isAuthenticated made it invisible in exactly the flow it exists for.
                //
                // NEVER shown to anyone with a subscription of any kind (Entitlement.hasAnySubscription:
                // unlimited, a search pack, active / canceling / past due / paused). freeAnalysesBannerText
                // holds the whole rule, shared with mobile: nothing while auth is still resolving
                // (isAuthenticated is transiently false on load), signed out = the visitor's allowance,
                // signed in = only once the entitlement has loaded, has no subscription and has free
                // analyses left (null otherwise).
                val freeNudgeText = com.islamicmedia.shared.domain.model.freeAnalysesBannerText(
                    isAuthenticated = isAuthenticated,
                    isAuthLoading = isAuthLoading,
                    entitlement = billingEntitlement,
                    signedOutLimit = uiState.freeFullLimit
                )
                if (currentView == ActiveView.SEARCH && AppConfig.PAYMENTS_ENABLED && freeNudgeText != null) {
                    FreeAnalysisNudge(freeNudgeText)
                }

                // A bulk-scan batch that is running or finished, reachable while the scan screens are closed.
                val bulkBatch = uiState.bulkBatch
                if (currentView == ActiveView.SEARCH && bulkBatch != null && uiState.bulkScan == null) {
                    BulkBatchBanner(
                        batch = bulkBatch,
                        onOpen = viewModel::openBulkScan,
                        onDismiss = viewModel::dismissBulkBatch,
                    )
                }

                // Multi-analysis: live list of in-flight + recently completed analyses,
                // merged with persisted history so the user sees their full corpus.
                if (showsStrip) {
                    ActiveAnalysesStrip(
                        // First login: the local cache is empty and the remote history sync takes a
                        // few seconds, so the strip would be absent with nothing to explain it.
                        // historyLoaded flips true even when the sync finds nothing, so a user with
                        // no analyses never gets a stuck "Loading…".
                        loading = isAuthenticated && !uiState.historyLoaded,
                        entries = stripEntries,
                        layout = uiState.displaySettings.analysesLayout,
                    )
                }

                // Founding member link — legacy external signup; hidden once in-app payments are on.
                if (!isAuthenticated && currentView == ActiveView.SEARCH && !AppConfig.PAYMENTS_ENABLED) {
                    Div(attrs = {
                        style {
                            property("text-align", "center")
                            property("margin-top", "14px")
                        }
                    }) {
                        A(href = "https://learn.thegoodchild.org/the-fitrah-filter/", attrs = {
                            attr("target", "_blank")
                            ref { el ->
                                val s = el.style
                                s.setProperty("display", "inline-block")
                                s.setProperty("background", "$Primary")
                                s.setProperty("color", "#DAA520")
                                s.setProperty("border", "2px solid #DAA520")
                                s.setProperty("border-radius", "24px")
                                s.setProperty("padding", "10px 24px")
                                s.setProperty("font-size", "16px")
                                s.setProperty("font-weight", "500")
                                s.setProperty("text-decoration", "none")
                                s.setProperty("text-shadow", "0 1px 4px rgba(0,60,30,0.6)")
                                s.setProperty("box-shadow", "0 4px 16px rgba(0,0,0,0.15)")
                                s.setProperty("cursor", "pointer")
                                s.setProperty("transition", "transform 0.2s ease, box-shadow 0.2s ease")
                                el.addEventListener("mouseenter", {
                                    s.setProperty("transform", "scale(1.07)")
                                    s.setProperty("box-shadow", "0 6px 24px rgba(0,0,0,0.25)")
                                })
                                el.addEventListener("mouseleave", {
                                    s.setProperty("transform", "scale(1)")
                                    s.setProperty("box-shadow", "0 4px 16px rgba(0,0,0,0.15)")
                                })
                                onDispose {}
                            }
                        }) {
                            Text("Click now to become a founding member")
                        }
                    }
                }

                // Lower region: the sliding reviews belt with an EQUAL flex spacer on each side, so
                // it sits centred in the gap between the search card and the App Store / Google
                // Play badges. Was 2.5 / 1.5, which parked the belt high in that gap.
                //
                // 2 + 2, NOT 1 + 1. These are not the only flex children of this column — the two
                // spacers above the search card (~L1035 / ~L1065) are `flex: 1` each. What fixes
                // the search card's position is this pair's share of the TOTAL, so the sum has to
                // stay at 4 (total 6). Halving them to 1 + 1 drops the total to 4, hands the upper
                // spacers 1/4 each instead of 1/6, and shoves the search card down the page.
                if (currentView == ActiveView.SEARCH) {
                    Div(attrs = { style { property("flex", "2 1 0"); property("min-height", "0") } })
                    // Reviews marquee — social proof for logged-out visitors only.
                    if (showVisitorMarquees) {
                        ReviewsBelt()
                    }
                    Div(attrs = { style { property("flex", "2 1 0"); property("min-height", "0") } })
                }
        }

        // Footer disclaimer
        DisclaimerFooter()


        // Confirm cancellation when the user taps X on a task pill.
        pendingCancelTaskId?.let { taskId ->
            val task = uiState.activeAnalyses[taskId]
            Div(attrs = {
                onClick { pendingCancelTaskId = null }
                style {
                    property("position", "fixed")
                    property("inset", "0")
                    property("background", "rgba(0,0,0,0.5)")
                    display(DisplayStyle.Flex)
                    alignItems(AlignItems.Center)
                    justifyContent(JustifyContent.Center)
                    property("z-index", "1000")
                }
            }) {
                Div(attrs = {
                    onClick { it.stopPropagation() }
                    style {
                        backgroundColor(Color.white)
                        borderRadius(20.px)
                        padding(28.px)
                        property("max-width", "400px")
                        width(90.percent)
                        property("box-shadow", "0 20px 60px rgba(0,0,0,0.3)")
                    }
                }) {
                    P(attrs = {
                        style {
                            margin(0.px, 0.px, 12.px)
                            fontSize(20.px)
                            fontWeight(700)
                            color(Color("#1a1a1a"))
                        }
                    }) { Text("Cancel Analysis?") }
                    P(attrs = {
                        style {
                            margin(0.px, 0.px, 24.px)
                            fontSize(15.px)
                            color(Gray500)
                            property("line-height", "1.5")
                        }
                    }) {
                        Text("This will cancel your analysis of ${task?.displayTitle ?: "this title"}. Are you sure?")
                    }
                    Div(attrs = {
                        style {
                            display(DisplayStyle.Flex)
                            justifyContent(JustifyContent.FlexEnd)
                            gap(12.px)
                        }
                    }) {
                        Button(attrs = {
                            onClick { pendingCancelTaskId = null }
                            style {
                                property("background", "transparent")
                                property("border", "none")
                                cursor("pointer")
                                fontSize(14.px)
                                fontWeight(600)
                                color(Primary)
                                padding(8.px, 16.px)
                                borderRadius(8.px)
                            }
                        }) { Text("Complete Analysis") }
                        Button(attrs = {
                            onClick {
                                val id = pendingCancelTaskId
                                pendingCancelTaskId = null
                                if (id != null) viewModel.cancelTask(id)
                            }
                            style {
                                property("background", "transparent")
                                property("border", "none")
                                cursor("pointer")
                                fontSize(14.px)
                                fontWeight(600)
                                color(ScoreRed)
                                padding(8.px, 16.px)
                                borderRadius(8.px)
                            }
                        }) { Text("Cancel Analysis") }
                    }
                }
            }
        }

        // Soft cap: tried to start a 6th simultaneously running analysis.
        if (uiState.showSoftCapWarning) {
            Div(attrs = {
                onClick { viewModel.dismissSoftCapWarning() }
                style {
                    property("position", "fixed")
                    property("inset", "0")
                    property("background", "rgba(0,0,0,0.5)")
                    display(DisplayStyle.Flex)
                    alignItems(AlignItems.Center)
                    justifyContent(JustifyContent.Center)
                    property("z-index", "1000")
                }
            }) {
                Div(attrs = {
                    onClick { it.stopPropagation() }
                    style {
                        backgroundColor(Color.white)
                        borderRadius(20.px)
                        padding(28.px)
                        property("max-width", "400px")
                        width(90.percent)
                        property("box-shadow", "0 20px 60px rgba(0,0,0,0.3)")
                    }
                }) {
                    P(attrs = {
                        style {
                            margin(0.px, 0.px, 12.px)
                            fontSize(20.px)
                            fontWeight(700)
                            color(Color("#1a1a1a"))
                        }
                    }) { Text("Too many at once") }
                    P(attrs = {
                        style {
                            margin(0.px, 0.px, 24.px)
                            fontSize(14.px)
                            color(Gray500)
                            property("line-height", "1.5")
                        }
                    }) {
                        Text("You can have up to 5 analyses running at the same time. Wait for one to finish or cancel it from the list.")
                    }
                    Div(attrs = {
                        style {
                            display(DisplayStyle.Flex)
                            justifyContent(JustifyContent.FlexEnd)
                        }
                    }) {
                        Button(attrs = {
                            onClick { viewModel.dismissSoftCapWarning() }
                            style {
                                property("background", "transparent")
                                property("border", "none")
                                cursor("pointer")
                                fontSize(14.px)
                                fontWeight(600)
                                color(Primary)
                                padding(8.px, 16.px)
                                borderRadius(8.px)
                            }
                        }) { Text("OK") }
                    }
                }
            }
        }

        // Daily fair-use cap reached. Can appear with no analysis task open (the entitlement
        // pre-flight raises it before a search even starts), so this lives at the top level
        // alongside the soft-cap warning rather than inside one of the currentView branches.
        uiState.dailyLimitNotice?.let { notice ->
            DailyLimitDialog(
                notice = notice,
                onDismiss = { viewModel.dismissDailyLimitNotice() },
                // "Request additional searches": a signed-in person's way to ask for more. Library
                // (search pack) accounts have their own dialog, and Kids Mode never reaches this page.
                canRequestMore = billingEntitlement?.isLibrary != true,
                requestState = uiState.limitRequest,
                onRequestMore = { viewModel.openLimitRequest() },
                onSendRequest = { viewModel.submitLimitRequest(it) },
                onCancelRequest = { viewModel.closeLimitRequest() },
            )
        }
        // A library / school account with no searches left: email us, not the paywall.
        if (uiState.showLibraryOutOfSearches) {
            LibraryOutOfSearchesDialog(message = viewModel.libraryOutOfSearchesMessage, onDismiss = { viewModel.dismissLibraryOutOfSearches() })
        }

        // Cancel analysis confirmation dialog
        if (showCancelDialog) {
            Div(attrs = {
                onClick { showCancelDialog = false }
                style {
                    property("position", "fixed")
                    property("inset", "0")
                    property("background", "rgba(0,0,0,0.5)")
                    display(DisplayStyle.Flex)
                    alignItems(AlignItems.Center)
                    justifyContent(JustifyContent.Center)
                    property("z-index", "1000")
                }
            }) {
                Div(attrs = {
                    onClick { it.stopPropagation() }
                    style {
                        backgroundColor(Color.white)
                        borderRadius(20.px)
                        padding(28.px)
                        property("max-width", "400px")
                        width(90.percent)
                        property("box-shadow", "0 20px 60px rgba(0,0,0,0.3)")
                    }
                }) {
                    // Title
                    P(attrs = {
                        style {
                            margin(0.px, 0.px, 12.px)
                            fontSize(20.px)
                            fontWeight(700)
                            color(Color("#1a1a1a"))
                        }
                    }) { Text("Cancel Analysis?") }

                    // Body
                    P(attrs = {
                        style {
                            margin(0.px, 0.px, 24.px)
                            fontSize(15.px)
                            color(Gray500)
                            property("line-height", "1.5")
                        }
                    }) {
                        Text("This will cancel your analysis of ${uiState.selectedTitle ?: uiState.searchQuery}. Are you sure?")
                    }

                    // Buttons
                    Div(attrs = {
                        style {
                            display(DisplayStyle.Flex)
                            justifyContent(JustifyContent.FlexEnd)
                            gap(12.px)
                        }
                    }) {
                        // Complete Analysis
                        Button(attrs = {
                            onClick { showCancelDialog = false }
                            style {
                                property("background", "transparent")
                                property("border", "none")
                                cursor("pointer")
                                fontSize(14.px)
                                fontWeight(600)
                                color(Primary)
                                padding(8.px, 16.px)
                                borderRadius(8.px)
                            }
                        }) { Text("Complete Analysis") }

                        // Cancel Analysis
                        Button(attrs = {
                            onClick {
                                showCancelDialog = false
                                // No onNewSearch() chain here. It used to be needed because
                                // onCancel() would resurrect a stale disambiguation list, and
                                // onNewSearch() cleared it as a side effect — which also threw away
                                // the list in the one case the user DOES want it back (cancel, then
                                // pick a different match). onCancel() now decides that itself, by
                                // journey, so mobile and web behave identically.
                                viewModel.onCancel()
                            }
                            style {
                                property("background", "transparent")
                                property("border", "none")
                                cursor("pointer")
                                fontSize(14.px)
                                fontWeight(600)
                                color(ScoreRed)
                                padding(8.px, 16.px)
                                borderRadius(8.px)
                            }
                        }) { Text("Cancel Analysis") }
                    }
                }
            }
        }
    }

    // Paywall — hoisted OUT of the content column's `when(currentView)` on purpose. Its backdrop is
    // position:fixed, and the card-slide-in wrapper in that column carries a permanent transform
    // (animation-fill-mode: both), which makes a transformed ancestor the containing block for
    // fixed positioning. Nested, the modal centred inside that wrapper's box and sat near the top of
    // the page. Out here its only ancestor chain is the page root, so it centres on the viewport.
    //
    // "Have a referral code?" on the paywall: signed in (the code attaches to an account), not
    // already a member, no code attached yet, and the program actually switched on. That last one
    // needs the server: for a non-member, /api/referral/me answers "not_eligible" while the program
    // is on and "disabled" while it's off. So ask each time the paywall opens, and keep the link
    // hidden until it answers (and if it fails) — a dark launch (REFERRALS_ENABLED=false) shows
    // nothing new. Mirrors mobile's MainScreen.
    val offerReferralCode = isAuthenticated &&
        billingEntitlement?.isActiveSubscriber != true &&
        uiState.referralAttachedCode == null
    val checkReferralProgram = currentView == ActiveView.PAYWALL && offerReferralCode
    LaunchedEffect(checkReferralProgram) {
        if (checkReferralProgram) viewModel.loadReferral()
    }
    val referralProgramOn = uiState.referralInfo?.let { it.isOk || it.isNotEligible } == true
    if (currentView == ActiveView.PAYWALL) {
        PaywallOverlay(
            title = uiState.selectedTitle,
            entitlement = billingEntitlement,
            isAuthenticated = isAuthenticated,
            onSubscribe = { planKey -> viewModel.startCheckout(planKey) },
            thriveCartUrl = viewModel.thriveCartCheckoutUrl(),
            onSignIn = { showSignupFromPaywall = true },
            onDismiss = {
                showSignupFromPaywall = false
                viewModel.onPaywallDismiss()
            },
            loading = billingLoading,
            plans = uiState.pricingPlans,
            // Signed-in only: the grant lands on an account, so there's nothing to redeem onto
            // until they have one.
            onHaveVoucher = if (isAuthenticated) viewModel::onOpenVoucherModal else null,
            // A friend's code: see offerReferralCode above for who gets the link.
            onHaveReferralCode = if (offerReferralCode && referralProgramOn) {
                viewModel::onOpenReferralCodeModal
            } else null,
            referralAppliedCode = uiState.referralAttachedCode,
            // Paying is NOT dismissing — onDismiss above discards the blocked search, which is
            // right for someone backing out and wrong for someone who just paid to finish it.
            onCheckoutSucceeded = viewModel::onCheckoutSucceeded,
            onCheckoutSuccessDismissed = viewModel::onCheckoutSuccessDismissed,
        )
    }

    // Voucher code entry — its own scrim stacked ABOVE the paywall, so dismissing returns the user
    // to the plans rather than to the search screen.
    if (uiState.showVoucherModal) {
        VoucherDialog(
            loading = uiState.voucherLoading,
            error = uiState.voucherError,
            onSubmit = viewModel::redeemVoucher,
            onDismiss = viewModel::onDismissVoucherModal
        )
    }

    // Referral code entry — stacked above the paywall like the voucher modal. Success closes it and
    // the paywall swaps its link for the "Referral applied" chip (uiState.referralAttachedCode).
    if (uiState.showReferralCodeModal) {
        ReferralCodeDialog(
            loading = uiState.referralCodeLoading,
            error = uiState.referralCodeError,
            onSubmit = viewModel::applyReferralCode,
            onDismiss = viewModel::onDismissReferralCodeModal
        )
    }

    // A friend's code entered AFTER subscribing — the Free Gift screen's "Got a code from a
    // friend?" link AND the proactive popup below share this one dialog (reuse, not a duplicate),
    // so it's rendered here rather than inside ReferralScreen. Same frame as [ReferralCodeDialog]
    // above, title swapped to the redeem wording. Mirrors mobile's MainScreen.
    if (uiState.showRedeemCodeModal) {
        ReferralCodeDialog(
            loading = uiState.redeemCodeLoading,
            error = uiState.redeemCodeError,
            onSubmit = viewModel::redeemReferralCode,
            onDismiss = viewModel::onDismissRedeemCodeModal,
            title = ReferralCopy.REDEEM_TITLE,
            initialCode = uiState.referralInfo?.redeemCode.orEmpty(),
        )
    }

    // Confirmation after a successful redeem, from either entry point above.
    if (uiState.redeemSucceeded) {
        ReferralRedeemSuccessModal(
            message = com.islamicmedia.shared.domain.model.formatReferralDate(uiState.redeemSuccessNextBill)
                ?.let { ReferralCopy.redeemSuccess(it) } ?: ReferralCopy.REDEEM_SUCCESS_NO_DATE,
            onConsumed = viewModel::onRedeemSuccessConsumed,
        )
    }

    // The proactive "Did a friend send you a code?" popup — see referralPromptScreenClear above
    // for when this is allowed to appear. "Enter code" opens the exact same dialog as "Got a code
    // from a friend?" on the Free Gift screen (right above); "No thanks" just closes it, the Free
    // Gift screen keeps offering the same entry until their first month ends. Same frame as the
    // cancel-analysis confirmation further down.
    if (showReferralRedeemPrompt) {
        val dismissPrompt = {
            showReferralRedeemPrompt = false
            viewModel.onReferralRedeemPromptDismissed()
        }
        Div(attrs = {
            onClick { dismissPrompt() }
            style {
                position(Position.Fixed); top(0.px); left(0.px); right(0.px); bottom(0.px)
                backgroundColor(rgba(0, 0, 0, 0.5))
                display(DisplayStyle.Flex); alignItems(AlignItems.Center); justifyContent(JustifyContent.Center)
                property("z-index", "1000")
            }
        }) {
            Div(attrs = {
                onClick { it.stopPropagation() }
                style {
                    backgroundColor(Color.white)
                    borderRadius(20.px)
                    padding(28.px)
                    property("max-width", "400px")
                    width(90.percent)
                    property("box-shadow", "0 20px 60px rgba(0,0,0,0.3)")
                    property("text-align", "center")
                }
            }) {
                Div(attrs = { style { fontSize(32.px); marginBottom(8.px) } }) { Text("🎁") }
                P(attrs = {
                    style { margin(0.px, 0.px, 12.px); fontSize(20.px); fontWeight(700); color(Color("#1a1a1a")) }
                }) { Text(ReferralCopy.REDEEM_PROMPT_TITLE) }
                P(attrs = {
                    style { margin(0.px, 0.px, 24.px); fontSize(14.px); color(Gray500); property("line-height", "1.5") }
                }) { Text(ReferralCopy.REDEEM_PROMPT_BODY) }
                Div(attrs = {
                    style { display(DisplayStyle.Flex); justifyContent(JustifyContent.Center); gap(12.px) }
                }) {
                    Button(attrs = {
                        onClick { dismissPrompt() }
                        style {
                            property("background", "transparent")
                            property("border", "none")
                            cursor("pointer")
                            fontSize(14.px)
                            fontWeight(600)
                            color(Gray500)
                            padding(8.px, 16.px)
                            borderRadius(8.px)
                        }
                    }) { Text(ReferralCopy.REDEEM_PROMPT_DISMISS) }
                    Button(attrs = {
                        onClick {
                            showReferralRedeemPrompt = false
                            viewModel.onReferralRedeemPromptEnterCode()
                        }
                        style {
                            property("border", "none")
                            cursor("pointer")
                            fontSize(14.px)
                            fontWeight(700)
                            color(Color.white)
                            backgroundColor(Primary)
                            padding(9.px, 20.px)
                            borderRadius(10.px)
                        }
                    }) { Text(ReferralCopy.REDEEM_PROMPT_ENTER) }
                }
            }
        }
    }

    // The one-time "your referral was blocked" notice — see referralPromptScreenClear above for
    // when this is allowed to appear (never together with the popup right above it).
    if (showReferralBlockedNotice) {
        val acknowledge = {
            showReferralBlockedNotice = false
            viewModel.onReferralBlockedNoticeAcknowledged()
        }
        Div(attrs = {
            onClick { acknowledge() }
            style {
                position(Position.Fixed); top(0.px); left(0.px); right(0.px); bottom(0.px)
                backgroundColor(rgba(0, 0, 0, 0.5))
                display(DisplayStyle.Flex); alignItems(AlignItems.Center); justifyContent(JustifyContent.Center)
                property("z-index", "1000"); padding(16.px)
            }
        }) {
            Div(attrs = {
                onClick { it.stopPropagation() }
                style {
                    backgroundColor(Color.white)
                    borderRadius(20.px)
                    padding(28.px)
                    property("max-width", "340px")
                    width(90.percent)
                    property("box-sizing", "border-box")
                    property("box-shadow", "0 20px 60px rgba(0,0,0,0.3)")
                    property("text-align", "center")
                }
            }) {
                P(attrs = {
                    style { margin(0.px, 0.px, 20.px); fontSize(15.px); color(Color("#1a1a1a")); property("line-height", "1.5") }
                }) { Text(ReferralCopy.REFERRAL_BLOCKED) }
                Button(attrs = {
                    onClick { acknowledge() }
                    style {
                        width(100.percent); property("border", "none"); cursor("pointer")
                        fontSize(14.px); fontWeight(700); color(Color.white); backgroundColor(Primary)
                        padding(10.px, 20.px); borderRadius(10.px)
                    }
                }) { Text(ReferralCopy.REFERRAL_BLOCKED_OK) }
            }
        }
    }

    // Confirmation after a successful redemption. The paywall has already closed itself and the
    // blocked analysis has resumed, so this is the only thing telling them it worked.
    uiState.voucherGrantedDays?.let { days ->
        Div(attrs = {
            onClick { viewModel.onVoucherGrantConsumed() }
            style {
                position(Position.Fixed); top(0.px); left(0.px); right(0.px); bottom(0.px)
                backgroundColor(rgba(0, 0, 0, 0.5))
                display(DisplayStyle.Flex); alignItems(AlignItems.Center); justifyContent(JustifyContent.Center)
                property("z-index", "3100"); padding(16.px)
            }
        }) {
            Div(attrs = {
                onClick { it.stopPropagation() }
                style {
                    width(100.percent); property("max-width", "340px")
                    backgroundColor(Color.white); borderRadius(20.px); padding(24.px)
                    property("box-sizing", "border-box"); property("text-align", "center")
                    property("box-shadow", "0 20px 60px -20px rgba(0,0,0,0.45)")
                }
            }) {
                Div(attrs = { style { fontSize(32.px) } }) { Text("🎉") }
                H3(attrs = {
                    style { margin(10.px, 0.px, 0.px); fontSize(18.px); fontWeight(700); color(Gray900) }
                }) { Text("You're all set") }
                P(attrs = {
                    style { margin(8.px, 0.px, 0.px); fontSize(14.px); color(Gray600) }
                }) {
                    Text(
                        if (days > 0) "Your voucher unlocked $days days of access."
                        else "Your voucher unlocked access."
                    )
                }
                Button(attrs = {
                    onClick { viewModel.onVoucherGrantConsumed() }
                    style {
                        property("margin-top", "18px"); width(100.percent); padding(12.px)
                        fontSize(15.px); fontWeight(700); color(Color.white); backgroundColor(Primary)
                        borderRadius(14.px); property("border", "none"); cursor("pointer")
                    }
                }) { Text("Great") }
            }
        }
    }

    // Plan switcher (subscriber tapped their plan pill) — reuses the paywall card in switch mode.
    billingEntitlement?.let { ent ->
        if (showPlanSwitcher) {
            PaywallOverlay(
                title = null,
                entitlement = ent,
                isAuthenticated = true,
                onSubscribe = {},
                onSignIn = {},
                onDismiss = { showPlanSwitcher = false },
                loading = billingLoading,
                currentPlan = ent.plan,
                onSwitchPlan = { planKey -> pendingSwitchPlan = planKey },
                plans = uiState.pricingPlans
            )
        }
    }

    // "Get credits" from a fully-canceled membership — reuses the paywall card in subscribe mode.
    if (showGetCredits) {
        PaywallOverlay(
            title = null,
            entitlement = billingEntitlement,
            isAuthenticated = true,
            onSubscribe = { planKey ->
                // Still in the grace window (live sub) → reactivate/switch it in place; a fresh
                // checkout would create a duplicate, overlapping subscription. Fully canceled →
                // checkout (server reuses the existing Stripe customer, so no duplicate customer).
                if (billingEntitlement?.canReactivate == true) viewModel.changePlan(planKey)
                else viewModel.startCheckout(planKey)
            },
            onSignIn = {},
            onDismiss = {
                showGetCredits = false
                // The embedded ThriveCart checkout grants server-side, so re-check on close —
                // otherwise someone who just paid still sees "No Active Subscription". Mirrors mobile.
                viewModel.onCheckoutSheetClosed()
            },
            loading = billingLoading,
            plans = uiState.pricingPlans,
            // ThriveCart mode applies here too (it was missing, so this path showed the Stripe paywall
            // while the main one sent buyers to ThriveCart). Except a subscription still in its grace
            // window: that is reactivated in place on Stripe; a ThriveCart purchase would duplicate it.
            thriveCartUrl = viewModel.thriveCartCheckoutUrl().takeIf { billingEntitlement?.canReactivate != true }
        )
    }

    // Confirm a plan switch before applying it.
    pendingSwitchPlan?.let { planKey ->
        val planName = uiState.pricingPlans.firstOrNull { it.key == planKey }?.name
            ?: "the new plan"
        Div(attrs = {
            onClick { pendingSwitchPlan = null }
            style {
                property("position", "fixed"); property("top", "0"); property("left", "0")
                property("right", "0"); property("bottom", "0")
                property("background", "rgba(15,23,42,0.55)")
                display(DisplayStyle.Flex); alignItems(AlignItems.Center); justifyContent(JustifyContent.Center)
                padding(20.px); property("box-sizing", "border-box"); property("z-index", "1100")
            }
        }) {
            Div(attrs = {
                onClick { event -> event.stopPropagation() }
                style {
                    property("width", "min(90%, 360px)"); backgroundColor(Color.white); borderRadius(20.px)
                    padding(24.px); property("text-align", "center"); property("box-sizing", "border-box")
                    property("box-shadow", CardElevatedShadow)
                }
            }) {
                H3(attrs = { style { margin(0.px); fontSize(18.px); fontWeight(700); color(Gray900) } }) {
                    Text("Switch to $planName?")
                }
                P(attrs = { style { color(Gray600); fontSize(14.px); margin(10.px, 0.px, 20.px) } }) {
                    Text("Your new plan starts today. What's left of your current plan is credited toward it, so you only pay the difference now. If the credit is more than the new price, the rest goes toward your next renewals.")
                }
                Div(attrs = { style { display(DisplayStyle.Flex); gap(10.px) } }) {
                    Button(attrs = {
                        onClick { pendingSwitchPlan = null }
                        style {
                            property("flex", "1"); padding(12.px); borderRadius(12.px)
                            property("border", "1px solid ${Gray200}"); property("background", "transparent")
                            color(Gray700); cursor("pointer"); fontWeight(600); fontSize(14.px)
                        }
                    }) { Text("Cancel") }
                    Button(attrs = {
                        onClick {
                            viewModel.changePlan(planKey)
                            pendingSwitchPlan = null
                            showPlanSwitcher = false
                        }
                        style {
                            property("flex", "1"); padding(12.px); borderRadius(12.px); property("border", "none")
                            property("background", GradientPrimary); color(Color.white)
                            cursor("pointer"); fontWeight(700); fontSize(14.px)
                        }
                    }) { Text("Switch") }
                }
            }
        }
    }
}

private enum class ActiveView {
    SEARCH, SEARCHING, DISAMBIGUATION, PROGRESS, RESULTS, HISTORY, PAYWALL, SIGNUP_FROM_PAYWALL, MENU, PROFILE, ACTIVITIES, ABOUT, HELP, REFERRAL, BULK_SCAN
}

/** The avatar menu and the screens it opens. They get the minimized header (small emblem top-left,
 *  level with the avatar) instead of the full hero — mirrors mobile's MenuViews. */
private val MenuViews = setOf(ActiveView.MENU, ActiveView.PROFILE, ActiveView.ABOUT, ActiveView.ACTIVITIES, ActiveView.HELP, ActiveView.REFERRAL, ActiveView.BULK_SCAN)

@Composable
private fun SearchWidget(
    uiState: com.islamicmedia.shared.presentation.MediaCheckerUiState,
    viewModel: MediaCheckerViewModel,
    userDisplayName: String? = null,
    isAuthenticated: Boolean = true,
    /** Reports the search box's own focus state up to MainContent — e.g. so the referral redeem
     *  popup's gate can wait for it to lose focus. */
    onFocusChanged: (Boolean) -> Unit = {},
) {
    var filtersExpanded by remember { mutableStateOf(false) }
    // Subscription billing balance — drives the compact credits chip above the search bar.
    val billingEntitlement by viewModel.entitlement.collectAsState()

    // Collapse the filter panel when switching to Quick Check
    LaunchedEffect(uiState.quickCheck) {
        if (uiState.quickCheck) filtersExpanded = false
    }

    // Content only — card wrapper is handled by the persistent card in Index()
    Div {
        SearchBar(
            query = uiState.searchQuery,
            onQueryChange = viewModel::onSearchQueryChanged,
            onSearch = viewModel::onSearch,
            onCancel = viewModel::onCancel,
            isSearching = uiState.isSearching,
            showProgress = false,
            filtersExpanded = filtersExpanded,
            onToggleFilters = { filtersExpanded = !filtersExpanded },
            onImagePicked = { viewModel.onImageSelected(it, fromCamera = false) },
            onCancelRecognition = viewModel::cancelRecognition,
            isRecognizingText = uiState.isRecognizingText,
            quickCheck = uiState.quickCheck,
            suggestions = uiState.suggestions,
            recentSearches = uiState.recentSearches,
            isAuthenticated = isAuthenticated,
            onSuggestionSelected = viewModel::onSuggestionSelected,
            onRecentSelected = viewModel::onRecentSearchSelected,
            onRemoveRecent = viewModel::removeRecentSearch,
            onFocusChanged = onFocusChanged
        )

        // Photo yielded several media items — let the user pick which one to analyze.
        ImageCandidatePicker(
            candidates = uiState.imageCandidates,
            onSelect = viewModel::onImageCandidateSelected,
            onDismiss = viewModel::dismissImageCandidates
        )

        SearchCriteriaPanel(
            criteria = uiState.searchCriteria,
            onCriteriaChange = viewModel::onSearchCriteriaChanged,
            expanded = filtersExpanded,
            quickCheck = uiState.quickCheck
        )
    }
}

@Composable
private fun BackendToggleBanner() {
    val active = AppConfig.activeEnvironment
    val isProd = active == Environment.PROD
    val bg = if (isProd) "#B91C1C" else "#0E7C7B"
    val label = if (isProd) "PROD BACKEND (live data)" else "DEV BACKEND"
    val switchTo = if (isProd) "DEV" else "PROD"

    Div(attrs = {
        style {
            width(100.percent)
            property("background", bg)
            color(Color.white)
            padding(10.px, 16.px)
            display(DisplayStyle.Flex)
            alignItems(AlignItems.Center)
            justifyContent(JustifyContent.Center)
            gap(16.px)
            fontSize(13.px)
            fontWeight(700)
            property("letter-spacing", "0.04em")
            property("text-transform", "uppercase")
            property("box-sizing", "border-box")
            property("box-shadow", "0 2px 8px rgba(0,0,0,0.15)")
        }
    }) {
        Span { Text("⚠ $label") }
        Button(attrs = {
            attr("type", "button")
            onClick {
                // Wipe all Supabase auth state — session tokens are keyed by project ref and
                // never carry across a backend switch. Without this, the new backend sees stale
                // tokens from the old project and rejects them.
                val ls = kotlinx.browser.window.localStorage
                val toRemove = mutableListOf<String>()
                for (i in 0 until ls.length) {
                    val k = ls.key(i) ?: continue
                    if (k.startsWith("sb-") || k.startsWith("supabase.")) toRemove.add(k)
                }
                toRemove.forEach { ls.removeItem(it) }
                ls.setItem("env-override", switchTo)
                kotlinx.browser.window.location.reload()
            }
            style {
                padding(6.px, 14.px)
                borderRadius(999.px)
                property("background", "rgba(255,255,255,0.18)")
                color(Color.white)
                property("border", "1px solid rgba(255,255,255,0.5)")
                fontSize(12.px)
                fontWeight(700)
                cursor("pointer")
                property("font-family", "inherit")
                property("letter-spacing", "0.04em")
                property("text-transform", "uppercase")
            }
        }) {
            Text("Switch to $switchTo")
        }
    }
}

@Composable
private fun QuickCheckToggle(
    quickCheck: Boolean,
    onChange: (Boolean) -> Unit,
    disabled: Boolean,
    quickCount: String? = null,
    fullCount: String? = null
) {
    Div(attrs = {
        style {
            display(DisplayStyle.Flex)
            justifyContent(JustifyContent.Center)
            marginTop(14.px)
            width(100.percent)
            property("box-sizing", "border-box")
        }
    }) {
        // Pill container — `position: relative` so the absolute-positioned thumb can slide
        // between the two segments. Both segments share equal width via `flex: 1` so the
        // 50%-wide thumb lands exactly under each one.
        Div(attrs = {
            id("cm-quickcheck") // coachmark anchor (first-run tour)
            style {
                property("position", "relative")
                display(DisplayStyle.Flex)
                // Bounded width so it never overflows a narrow phone viewport; a definite width
                // also lets `flex: 1 1 0` split the two segments exactly 50/50 (the thumb is 50%).
                width(100.percent)
                property("max-width", "380px")
                property("box-sizing", "border-box")
                property("background", "rgba(255, 255, 255, 0.7)")
                property("backdrop-filter", "blur(8px)")
                borderRadius(999.px)
                padding(3.px)
                fontSize(12.px)
                fontWeight(600)
                property("box-shadow", "0 2px 8px rgba(0,0,0,0.08)")
            }
        }) {
            // Sliding thumb — translates between the two halves and morphs background
            // (yellow → green gradient) on the same timing.
            Div(attrs = {
                style {
                    property("position", "absolute")
                    property("top", "3px")
                    property("bottom", "3px")
                    property("left", "3px")
                    property("width", "calc(50% - 3px)")
                    borderRadius(999.px)
                    property("background", if (quickCheck) "#FACC15" else "linear-gradient(135deg, #0E7C7B 0%, #14A3A0 100%)")
                    property("transform", if (quickCheck) "translateX(0%)" else "translateX(100%)")
                    property("transition", "transform 280ms cubic-bezier(0.4, 0, 0.2, 1), background 280ms ease")
                    property("z-index", "0")
                    property("box-shadow", "0 1px 4px rgba(0,0,0,0.12)")
                    property("pointer-events", "none")
                }
            }) {}

            QuickCheckSegment(
                label = "Quick check",
                selected = quickCheck,
                isDisabled = disabled,
                activeTextColor = "#4B5563",
                showQuickIcon = true,
                italic = true,
                creditCount = quickCount
            ) { onChange(true) }
            QuickCheckSegment(
                label = "Full analysis",
                selected = !quickCheck,
                isDisabled = disabled,
                activeTextColor = "#FFFFFF",
                creditCount = fullCount
            ) { onChange(false) }
        }
    }
}

@Composable
private fun QuickCheckSegment(
    label: String,
    selected: Boolean,
    isDisabled: Boolean,
    activeTextColor: String,
    showQuickIcon: Boolean = false,
    italic: Boolean = false,
    creditCount: String? = null,
    onTap: () -> Unit
) {
    Button(attrs = {
        if (isDisabled) {
            attr("disabled", "true")
        } else {
            onClick { onTap() }
        }
        style {
            padding(6.px, 12.px)
            borderRadius(999.px)
            property("border", "none")
            // Background is always transparent — the colored pill is the sliding thumb behind us
            property("background", "transparent")
            property("color", if (selected) activeTextColor else "#6B7280")
            cursor(if (isDisabled) "not-allowed" else "pointer")
            property("transition", "color 280ms ease, font-weight 280ms ease")
            property("font-family", "inherit")
            fontSize(11.px)
            fontWeight(if (selected) 700 else 500)
            property("opacity", if (isDisabled) "0.5" else "1")
            property("display", "inline-flex")
            alignItems(AlignItems.Center)
            justifyContent(JustifyContent.Center)
            gap(4.px)
            // Sit above the sliding thumb
            property("position", "relative")
            property("z-index", "1")
            // Equal 50/50 halves so the 50%-wide thumb aligns under each. With the pill given a
            // definite width (above), `flex: 1 1 0` + `min-width: 0` divides it exactly in two and
            // lets each half shrink to fit narrow viewports instead of forcing an overflow.
            property("flex", "1 1 0")
            property("min-width", "0")
            property("white-space", "nowrap")
            if (italic) property("font-style", "italic")
        }
    }) {
        if (showQuickIcon) {
            Img(src = "/images/quick.png", attrs = {
                style {
                    height(18.px)
                    property("width", "auto")
                }
            })
        }
        Text(label)
    }
}

@Composable
private fun ResultsWidget(
    uiState: com.islamicmedia.shared.presentation.MediaCheckerUiState,
    viewModel: MediaCheckerViewModel,
    // Comments/reactions: signed-in gate + the caller's own first name (snapshotted onto any
    // comment they post) — sourced from MainContent's auth state, not part of uiState.
    isSignedIn: Boolean = true,
    firstName: String? = null,
) {
    val viewed = uiState.viewedTask
    // The parent-side "what did my child see?" flip exists only for an account KIDZONE is switched on for.
    val kidsAllowed = viewModel.entitlement.collectAsState().value?.kidsMode == true
    // Fall back to the streamed Phase 1 verdict while Phase 2 is still running. `result` wins as
    // soon as it exists, so the swap to the fully-enriched version happens on its own.
    val result = uiState.viewingHistoryItem?.result
        ?: viewed?.result
        ?: viewed?.phase1PartialResult
        ?: return
    val displayTitle = uiState.viewingHistoryItem?.title
        ?: viewed?.displayTitle
        ?: uiState.selectedTitle
    val flagMediaType = viewed?.mediaType ?: uiState.viewingHistoryItem?.mediaType
    val isFlagged = uiState.flaggedAnalyses.contains(
        com.islamicmedia.shared.presentation.flagKey(displayTitle ?: "", flagMediaType)
    )
    val criteria = viewed?.criteria ?: uiState.searchCriteria
    val headerCoverUrl = result.coverUrl ?: viewed?.item?.coverUrl
    val analysisDurationMs = viewed?.let { task ->
        task.completedAtMs?.let { it - task.startedAtMs }
    }
    val retryingUnits = viewed?.retryingUnits ?: emptySet()
    // Flag-a-problematic-analysis modal state.
    var showFlagModal by remember { mutableStateOf(false) }
    var flagMessage by remember { mutableStateOf("") }
    var flagSubmitting by remember { mutableStateOf(false) }
    var flagSubmitted by remember { mutableStateOf(false) }
    var flagError by remember { mutableStateOf(false) }

    val onArt = !headerCoverUrl.isNullOrBlank()

    // Verdict-tinted sheet + border (green / amber / red by score) — matches the /isitsafe card
    // and mobile. The cover sits in a hero banner at the TOP, fading via a gradient foot into this
    // opaque sheet, which carries the title + all analysis content (legible, never on raw art).
    // Uniform neutral sheet for EVERY analysis, regardless of verdict (matches mobile's Gray50).
    // The border still carries the verdict colour.
    val sheetRgb = "245, 245, 245"
    val borderHex = when {
        result.score >= 7 -> "#22C55E"
        result.score >= 4 -> "#F59E0B"
        else -> "#EF4444"
    }

    Div(attrs = {
        style {
            property("flex", "1")
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            property("min-height", "0")
            marginBottom(8.px)
        }
    }) {
        // One card, two faces: for a Kids Mode check the card flips to show what the child saw. At rest
        // (and for every other analysis) the card carries no flip class and no extra element.
        val kidItem = uiState.viewingHistoryItem?.takeIf { it.kidScan && kidsAllowed }
        androidx.compose.runtime.DisposableEffect(Unit) { onDispose { viewModel.clearKidsPreview() } }
        val kidFlip = com.islamicmedia.site.components.rememberKidsFlip(kidItem != null && uiState.kidsFlipped)
        // Single card — verdict-tinted, fixed height, content scrolls inside (hero cover at top).
        Div(attrs = {
            kidFlip.cardClass?.let { classes(it) }
            style {
                property("background", "rgb($sheetRgb)")
                property("border", "4px solid $borderHex")
                borderRadius(RadiusXLarge.unsafeCast<CSSNumeric>())
                property("box-shadow", NeuBoxShadow)
                property("position", "relative")
                property("flex", "1")
                display(DisplayStyle.Flex)
                flexDirection(FlexDirection.Column)
                property("overflow", "hidden")
                property("min-height", "0")
            }
        }) {
            if (kidItem != null && kidFlip.showBack) {
                com.islamicmedia.site.components.KidsPreviewBackFace(
                    item = kidItem,
                    state = uiState.kidsPreview ?: com.islamicmedia.shared.presentation.KidsPreviewState.Loading,
                    onRetry = { viewModel.openKidsPreview(kidItem) },
                    onFlipBack = { viewModel.closeKidsPreview() },
                    band = viewModel.kids.state.value.band
                )
            }
            // NOTE: the flag affordance used to float here (top-left, over the cover art). It
            // now lives at the right of the "How would you rate this analysis?" row inside the
            // result content, so all the feedback controls sit together. See CommentsSection.

            // Close/New Search button
            Button(attrs = {
                classes("close-btn")
                onClick { viewModel.onNewSearch() }
                style {
                    property("position", "absolute")
                    property("top", "16px")
                    property("right", "16px")
                    width(34.px)
                    height(34.px)
                    borderRadius(50.percent)
                    // White circle so the \u2715 stays visible over the hero art and the light sheet.
                    property("background", "rgba(255,255,255,0.92)")
                    property("box-shadow", "0 2px 6px rgba(0,0,0,0.18)")
                    property("border", "none")
                    cursor("pointer")
                    display(DisplayStyle.Flex)
                    alignItems(AlignItems.Center)
                    justifyContent(JustifyContent.Center)
                    fontSize(18.px)
                    property("color", "#444")
                    property("transition", "all 0.2s ease")
                    property("z-index", "10")
                }
            }) { Text("\u2715") }

            // Scrollable content area \u2014 the cover hero sits at the TOP and scrolls with (and
            // stretches on pull-down) the content; title + analysis live on the verdict-tinted
            // sheet below, so text is always legible. Matches the /isitsafe card and mobile.
            Div(attrs = {
                style {
                    property("position", "relative")
                    property("z-index", "1")
                    property("flex", "1")
                    property("overflow-y", "auto")
                    property("min-height", "0")
                    // Make the analysis result text unselectable (matches mobile, where
                    // Compose Text is non-selectable without a SelectionContainer).
                    property("user-select", "none")
                    property("-webkit-user-select", "none")
                }
            }) {
                // Cover hero banner \u2014 top only, fading via a gradient foot into the verdict sheet.
                // This is the element the pull gesture stretches.
                val rawTitle = displayTitle ?: "Analysis"
                val yearMatch = Regex("""\((\d{4})\)\s*$""").find(rawTitle)
                val cleanTitle = if (yearMatch != null)
                    rawTitle.substring(0, yearMatch.range.first).trim()
                else rawTitle
                val year = yearMatch?.groupValues?.get(1) ?: viewed?.item?.year
                val authorYear = listOfNotNull(
                    (result.author ?: viewed?.item?.author)?.takeIf { it.isNotBlank() },
                    year?.takeIf { it.isNotBlank() }
                ).joinToString(" • ")

                Div(attrs = {
                    style {
                        property("position", "relative")
                        property("overflow", "hidden")
                    }
                }) {
                    Div(attrs = {
                        style {
                            property("position", "absolute")
                            property("inset", "0")
                            if (onArt) {
                                property("background-image", "url($headerCoverUrl)")
                                property("background-size", "cover")
                                property("background-position", "center 30%")
                            } else {
                                property("background", "linear-gradient(135deg, #F8F8F4 0%, #FFFFFF 50%, #FAF5EA 100%)")
                            }
                        }
                    }) {}
                    // Flow content over the cover: art region, then title, then the gradient foot.
                    Div(attrs = { style { property("position", "relative") } }) {
                        Div(attrs = { style { property("height", "250px") } }) {}
                        // White text on a dark scrim ramp so it's legible on any cover.
                        Div(attrs = {
                            style {
                                property("text-align", "center")
                                padding(40.px, 24.px, 0.px)
                                property("background", "linear-gradient(rgba(0,0,0,0) 0%, rgba(0,0,0,0.55) 100%)")
                            }
                        }) {
                            H2(attrs = {
                                style {
                                    margin(0.px)
                                    property("font-family", TitleFontFamily)
                                    fontSize(24.px)
                                    fontWeight(700)
                                    property("line-height", "1.25")
                                    property("color", "#ffffff")
                                    property("text-shadow", "0 1px 8px rgba(0,0,0,0.65)")
                                }
                            }) {
                                Text(cleanTitle)
                            }
                            if (authorYear.isNotBlank()) {
                                Div(attrs = {
                                    style {
                                        marginTop(4.px)
                                        fontSize(14.px)
                                        fontWeight(500)
                                        property("color", "rgba(255,255,255,0.92)")
                                        property("text-shadow", "0 1px 8px rgba(0,0,0,0.65)")
                                    }
                                }) {
                                    Text(authorYear)
                                }
                            }
                        }
                        // Gradient foot - fades the dark scrim into the sheet, below the title.
                        Div(attrs = {
                            style {
                                property("height", "96px")
                                property("background", "linear-gradient(rgba(0,0,0,0.55) 0%, rgb($sheetRgb) 100%)")
                            }
                        }) {}
                    }
                }

                // Verdict-tinted content sheet (title now lives over the cover above).
                Div(attrs = {
                    style {
                        property("background", "rgb($sheetRgb)")
                        padding(4.px, 28.px, 32.px)
                    }
                }) {

                    // Parent-side "what did my child see?": a button on a Kids Mode check; the card itself flips (see kidFlip).
                    uiState.viewingHistoryItem?.takeIf { it.kidScan && kidsAllowed }?.let { kidItem ->
                        com.islamicmedia.site.components.KidsPreviewButton(
                            verdict = (uiState.kidsPreview as? com.islamicmedia.shared.presentation.KidsPreviewState.Ready)?.result?.kidsVerdict
                        ) { viewModel.openKidsPreview(kidItem) }
                    }

                    // Scrolling results content — wrapped so the user's Display style (analysis text
                    // size + color) applies.
                    com.islamicmedia.site.components.DisplayStyled(uiState.displaySettings) {
                    CombinedAnalysisResultContent(
                        result = result,
                        // Credit system retired — clicking "Full analysis available" just runs it,
                        // no confirmation popup.
                        onRunFullAnalysis = if (result.quickCheck) {
                            { viewModel.runFullAnalysisForCurrent() }
                        } else null,
                        criteria = criteria,
                        onRetryConcern = { unit -> viewModel.retryConcernUnit(unit) },
                        analysisDurationMs = analysisDurationMs,
                        retryingUnits = retryingUnits,
                        tldrLoading = uiState.highlightsLoading,
                        alternatives = uiState.alternatives,
                        onAlternativeSelected = { item, isQuick -> viewModel.onAlternativeSelected(item, isQuick) },
                        islamicAlternatives = uiState.islamicAlternatives,
                        authorAlternatives = uiState.authorAlternatives,
                        title = displayTitle,
                        mediaType = flagMediaType,
                        year = year,
                        comments = uiState.comments,
                        likeCount = uiState.likeCount,
                        dislikeCount = uiState.dislikeCount,
                        myReaction = uiState.myReaction,
                        isSignedIn = isSignedIn,
                        isSubmittingComment = uiState.isSubmittingComment,
                        onReact = { reaction -> flagMediaType?.let { viewModel.setReaction(cleanTitle, it, year, reaction) } },
                        onPostComment = { body -> flagMediaType?.let { viewModel.postComment(cleanTitle, it, year, body, firstName ?: "You") } },
                        onEditComment = { id, body -> viewModel.editComment(id, body) },
                        onDeleteComment = { id -> viewModel.deleteComment(id) },
                        isFlagged = isFlagged,
                        commentActionError = uiState.commentActionError,
                        onFlagClick = {
                            showFlagModal = true; flagSubmitted = false; flagError = false; flagMessage = ""
                        },
                    )
                    }
                }
            }
        }
    }

    // Orb keyframe animations \u2014 shared with ProgressTracker so they're
    // available when the results view renders without the analyzing view.
    InjectOrbKeyframes()
    InjectStyles(
        "results-view-styles",
        """
        @keyframes quickBadgeIn {
            from { opacity: 0; transform: translateY(12px); }
            to   { opacity: 1; transform: translateY(0); }
        }
        """.trimIndent()
    )

    // Flag-a-problematic-analysis modal.
    if (showFlagModal) {
        Div(attrs = {
            onClick { if (!flagSubmitting) showFlagModal = false }
            style {
                property("position", "fixed"); property("top", "0"); property("left", "0")
                property("right", "0"); property("bottom", "0")
                property("background", "rgba(15,23,42,0.55)")
                display(DisplayStyle.Flex); alignItems(AlignItems.Center); justifyContent(JustifyContent.Center)
                padding(20.px); property("box-sizing", "border-box"); property("z-index", "1100")
            }
        }) {
            Div(attrs = {
                onClick { event -> event.stopPropagation() }
                style {
                    property("width", "min(92%, 420px)"); backgroundColor(Color.white); borderRadius(20.px)
                    padding(24.px); property("box-sizing", "border-box")
                    property("box-shadow", CardElevatedShadow)
                }
            }) {
                if (flagSubmitted) {
                    H3(attrs = { style { margin(0.px); fontSize(18.px); fontWeight(700); color(Gray900); property("text-align", "center") } }) {
                        Text("Thanks for flagging this")
                    }
                    P(attrs = { style { color(Gray600); fontSize(14.px); margin(10.px, 0.px, 20.px); property("text-align", "center") } }) {
                        Text("We'll review this analysis. Your report helps us improve.")
                    }
                    Button(attrs = {
                        onClick { showFlagModal = false }
                        style {
                            width(100.percent); padding(12.px); borderRadius(12.px); property("border", "none")
                            property("background", GradientPrimary); color(Color.white)
                            cursor("pointer"); fontWeight(700); fontSize(14.px)
                        }
                    }) { Text("Done") }
                } else {
                    H3(attrs = { style { margin(0.px); fontSize(18.px); fontWeight(700); color(Gray900) } }) {
                        Text("Flag this analysis")
                    }
                    P(attrs = { style { color(Gray600); fontSize(14.px); margin(10.px, 0.px, 14.px) } }) {
                        Text("Tell us what's wrong with this result (optional). We'll take a look.")
                    }
                    TextArea(value = flagMessage, attrs = {
                        onInput { flagMessage = it.value }
                        attr("rows", "4")
                        attr("placeholder", "What seems off about this analysis?")
                        style {
                            width(100.percent); property("box-sizing", "border-box")
                            padding(12.px); borderRadius(12.px)
                            property("border", "1.5px solid ${Gray300}")
                            fontSize(14.px); property("font-family", "inherit"); property("resize", "vertical")
                            property("outline", "none")
                            property("margin-bottom", if (flagError) "8px" else "20px")
                        }
                    })
                    if (flagError) {
                        P(attrs = { style { color(Color("#dc2626")); fontSize(13.px); margin(0.px, 0.px, 16.px) } }) {
                            Text("Couldn't submit. Please check your connection and try again.")
                        }
                    }
                    Div(attrs = { style { display(DisplayStyle.Flex); gap(10.px) } }) {
                        Button(attrs = {
                            if (flagSubmitting) attr("disabled", "true")
                            onClick { showFlagModal = false }
                            style {
                                property("flex", "1"); padding(12.px); borderRadius(12.px)
                                property("border", "1px solid ${Gray200}"); property("background", "transparent")
                                color(Gray700); cursor("pointer"); fontWeight(600); fontSize(14.px)
                            }
                        }) { Text("Cancel") }
                        Button(attrs = {
                            if (flagSubmitting) attr("disabled", "true")
                            onClick {
                                flagSubmitting = true
                                flagError = false
                                viewModel.flagAnalysis(flagMessage) { success ->
                                    flagSubmitting = false
                                    if (success) flagSubmitted = true else flagError = true
                                }
                            }
                            style {
                                property("flex", "1"); padding(12.px); borderRadius(12.px); property("border", "none")
                                property("background", "#dc2626"); color(Color.white)
                                cursor("pointer"); fontWeight(700); fontSize(14.px)
                                if (flagSubmitting) property("opacity", "0.6")
                            }
                        }) { Text(if (flagSubmitting) "Submitting…" else "Submit flag") }
                    }
                }
            }
        }
    }
}

@Composable
private fun SignupPrompt(
    authViewModel: AuthViewModel,
    selectedTitle: String,
    onClose: () -> Unit
) {
    val authState by authViewModel.uiState.collectAsState()

    // Default to sign-up mode (new-user-first)
    LaunchedEffect(Unit) {
        if (!authViewModel.uiState.value.isSignUp) {
            authViewModel.toggleSignUp()
        }
        // Funnel step 3 — web's twin of App.kt's "auth" screen report.
        com.islamicmedia.shared.platform.Funnel.track(
            com.islamicmedia.shared.platform.Event.AUTH_SHOWN,
            com.islamicmedia.shared.platform.FunnelStep.AUTH_SHOWN
        )
    }
    val isSignUp = authState.isSignUp
    var confirmPassword by remember { mutableStateOf("") }
    var passwordVisible by remember { mutableStateOf(false) }

    Div(attrs = {
        style {
            padding(32.px)
            property("text-align", "center")
        }
    }) {
        // Top strip reserved for the card-corner close X so it doesn't overlap the tabs below.
        Div(attrs = { style { height(20.px) } }) {}

        // Legacy founding-member external signup — hidden once in-app payments are on.
        if (!AppConfig.PAYMENTS_ENABLED) {
        Span(attrs = { style { color(Gray500); fontSize(12.px) } }) { Text("Not a founding member yet?") }
        Div(attrs = { style { height(6.px) } }) {}
        A(href = "https://learn.thegoodchild.org/the-fitrah-filter/", attrs = {
            attr("target", "_blank")
            ref { el ->
                val s = el.style
                s.setProperty("display", "inline-block")
                s.setProperty("background", "$Primary")
                s.setProperty("color", "#E8C860")
                s.setProperty("border", "2px solid #E8C860")
                s.setProperty("border-radius", "24px")
                s.setProperty("padding", "8px 20px")
                s.setProperty("font-size", "14px")
                s.setProperty("font-weight", "500")
                s.setProperty("text-decoration", "none")
                s.setProperty("box-shadow", "0 2px 8px rgba(0,0,0,0.12)")
                s.setProperty("cursor", "pointer")
                s.setProperty("transition", "transform 0.2s ease, box-shadow 0.2s ease")
                el.addEventListener("mouseenter", {
                    s.setProperty("transform", "scale(1.05)")
                    s.setProperty("box-shadow", "0 4px 16px rgba(0,0,0,0.2)")
                })
                el.addEventListener("mouseleave", {
                    s.setProperty("transform", "scale(1)")
                    s.setProperty("box-shadow", "0 2px 8px rgba(0,0,0,0.12)")
                })
                onDispose {}
            }
        }) { Text("Become a Founding Member") }

        // Divider after founding member
        Div(attrs = { style { width(100.percent); height(1.px); backgroundColor(Gray200); property("margin", "20px auto 20px") } }) {}

        Div(attrs = { style { color(Gray500); fontSize(12.px); property("margin-bottom", "8px") } }) { Text("Already a founding member?") }
        }

        // Contextual message — names the title the user tried to analyze (mirrors mobile auth card).
        if (selectedTitle.isNotBlank()) {
            val analyzeTitle = selectedTitle.replace(Regex("\\s*\\(\\d{4}\\)\\s*$"), "").trim()
            P(attrs = {
                style {
                    color(Gray500)
                    fontSize(14.px)
                    margin(0.px, 0.px, 12.px)
                }
            }) {
                Text(
                    // Sign-UP only promises the free analysis: a brand-new account always has it.
                    // Sign-IN deliberately doesn't — a returning user may already have spent theirs,
                    // and we can't know which until they authenticate.
                    if (isSignUp) "Sign up to complete your free analysis of $analyzeTitle"
                    else "Sign in to analyze $analyzeTitle"
                )
            }
        }

        // Auth form
        Div(attrs = {
            style {
                property("max-width", "320px")
                property("margin", "0 auto")
            }
        }) {
            // Google Sign In — primary option, above the email form
            Button(attrs = {
                onClick { authViewModel.signInWithGoogle(authReturnUrl()) }
                style {
                    width(100.percent)
                    property("height", "56px")
                    backgroundColor(Color.white)
                    borderRadius(12.px)
                    property("border", "1px solid ${Gray200}")
                    cursor("pointer")
                    display(DisplayStyle.Flex)
                    alignItems(AlignItems.Center)
                    justifyContent(JustifyContent.Center)
                    gap(10.px)
                }
            }) {
                Img(src = "https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg", attrs = {
                    style { width(20.px); height(20.px) }
                })
                Span(attrs = { style { fontSize(15.px); fontWeight(600); color(Gray700) } }) {
                    Text("Continue with Google")
                }
            }

            // OR divider (between Google and the email form)
            Div(attrs = {
                style {
                    display(DisplayStyle.Flex)
                    alignItems(AlignItems.Center)
                    gap(12.px)
                    property("margin", "16px 0")
                }
            }) {
                Div(attrs = { style { property("flex", "1"); height(1.px); backgroundColor(Gray200) } })
                Span(attrs = { style { color(Gray500); fontSize(13.px); property("letter-spacing", "0.5px") } }) { Text("OR") }
                Div(attrs = { style { property("flex", "1"); height(1.px); backgroundColor(Gray200) } })
            }

            // Tab toggle: Sign Up | Log In — below the Google button / OR separator
            Div(attrs = {
                style {
                    property("margin", "0 0 16px")
                    display(DisplayStyle.Flex)
                    borderRadius(12.px)
                    backgroundColor(Gray100)
                    padding(4.px)
                }
            }) {
                listOf(true to "Sign Up", false to "Log In").forEach { (signUp, label) ->
                    Div(attrs = {
                        onClick { if (isSignUp != signUp) authViewModel.toggleSignUp() }
                        style {
                            property("flex", "1")
                            property("text-align", "center")
                            padding(10.px)
                            borderRadius(10.px)
                            fontSize(14.px)
                            cursor("pointer")
                            property("transition", "all 0.2s ease")
                            if (isSignUp == signUp) {
                                backgroundColor(Color.white)
                                fontWeight(600)
                                color(Primary)
                                property("box-shadow", "0 1px 3px rgba(0,0,0,0.1)")
                            } else {
                                backgroundColor(Color.transparent)
                                fontWeight(400)
                                color(Gray500)
                            }
                        }
                    }) { Text(label) }
                }
            }

            // Name fields (sign up only)
            if (isSignUp) {
                Div(attrs = {
                    style {
                        display(DisplayStyle.Flex)
                        gap(8.px)
                        marginBottom(12.px)
                    }
                }) {
                    Input(type = org.jetbrains.compose.web.attributes.InputType.Text) {
                        value(authState.firstName)
                        onInput { authViewModel.onFirstNameChanged(it.value) }
                        attr("placeholder", "First Name")
                        style {
                            property("flex", "1")
                            property("min-width", "0")
                            padding(14.px, 16.px)
                            fontSize(15.px)
                            borderRadius(12.px)
                            property("border", "1px solid ${Gray200}")
                            property("outline", "none")
                            property("box-sizing", "border-box")
                        }
                    }
                    Input(type = org.jetbrains.compose.web.attributes.InputType.Text) {
                        value(authState.lastName)
                        onInput { authViewModel.onLastNameChanged(it.value) }
                        attr("placeholder", "Last Name")
                        style {
                            property("flex", "1")
                            property("min-width", "0")
                            padding(14.px, 16.px)
                            fontSize(15.px)
                            borderRadius(12.px)
                            property("border", "1px solid ${Gray200}")
                            property("outline", "none")
                            property("box-sizing", "border-box")
                        }
                    }
                }
            }

            // Email
            Input(type = org.jetbrains.compose.web.attributes.InputType.Email) {
                value(authState.email)
                onInput { authViewModel.onEmailChanged(it.value) }
                attr("placeholder", "Email address")
                style {
                    width(100.percent)
                    padding(14.px, 16.px)
                    fontSize(15.px)
                    borderRadius(12.px)
                    property("border", "1px solid ${Gray200}")
                    property("outline", "none")
                    property("box-sizing", "border-box")
                    marginBottom(12.px)
                }
            }

            // Password (with show/hide toggle)
            Div(attrs = {
                style {
                    property("position", "relative")
                    width(100.percent)
                    marginBottom(8.px)
                }
            }) {
                Input(type = if (passwordVisible) org.jetbrains.compose.web.attributes.InputType.Text else org.jetbrains.compose.web.attributes.InputType.Password) {
                    value(authState.password)
                    onInput { authViewModel.onPasswordChanged(it.value) }
                    attr("placeholder", "Password")
                    style {
                        width(100.percent)
                        padding(14.px, 16.px)
                        property("padding-right", "60px")
                        fontSize(15.px)
                        borderRadius(12.px)
                        property("border", "1px solid ${Gray200}")
                        property("outline", "none")
                        property("box-sizing", "border-box")
                    }
                }
                Span(attrs = {
                    onClick { passwordVisible = !passwordVisible }
                    style {
                        property("position", "absolute")
                        property("right", "14px")
                        property("top", "50%")
                        property("transform", "translateY(-50%)")
                        color(Gray500)
                        fontSize(12.px)
                        cursor("pointer")
                        property("user-select", "none")
                    }
                }) { Text(if (passwordVisible) "Hide" else "Show") }
            }

            // Confirm password (sign up only)
            if (isSignUp) {
                Input(type = org.jetbrains.compose.web.attributes.InputType.Password) {
                    value(confirmPassword)
                    onInput { confirmPassword = it.value }
                    attr("placeholder", "Confirm Password")
                    style {
                        width(100.percent)
                        padding(14.px, 16.px)
                        fontSize(15.px)
                        borderRadius(12.px)
                        property("border", if (confirmPassword.isNotEmpty() && confirmPassword != authState.password) "1px solid $ScoreRed" else "1px solid $Gray200")
                        property("outline", "none")
                        property("box-sizing", "border-box")
                        marginBottom(4.px)
                    }
                }
                if (confirmPassword.isNotEmpty() && confirmPassword != authState.password) {
                    Div(attrs = { style { color(ScoreRed); fontSize(12.px); marginBottom(8.px) } }) {
                        Text("Passwords don't match")
                    }
                } else {
                    Div(attrs = { style { height(12.px) } }) {}
                }
            }

            // Error
            authState.error?.let { error ->
                Div(attrs = {
                    style {
                        color(ScoreRed)
                        fontSize(13.px)
                        marginBottom(12.px)
                    }
                }) { Text(error) }
            }

            val passwordsMatch = !isSignUp || (confirmPassword == authState.password && confirmPassword.isNotEmpty())

            // Forgot password (log in only) — right-aligned, between password and submit
            if (!isSignUp) {
                Div(attrs = {
                    style {
                        property("text-align", "right")
                        marginBottom(12.px)
                    }
                }) {
                    Span(attrs = {
                        onClick { authViewModel.resetPassword() }
                        style {
                            color(Primary)
                            fontSize(13.px)
                            fontWeight(600)
                            cursor("pointer")
                        }
                    }) { Text("Forgot password?") }
                }
            }

            // Submit button
            Button(attrs = {
                onClick { if (passwordsMatch) authViewModel.onSubmit() }
                if (!passwordsMatch || authState.isSubmitting) attr("disabled", "")
                style {
                    width(100.percent)
                    padding(14.px)
                    fontSize(15.px)
                    fontWeight(600)
                    color(Color.white)
                    property("background", if (passwordsMatch && !authState.isSubmitting) GradientPrimary else "$Gray400")
                    borderRadius(12.px)
                    property("border", "none")
                    cursor(if (passwordsMatch) "pointer" else "not-allowed")
                    property("opacity", if (passwordsMatch && !authState.isSubmitting) "1" else "0.6")
                }
            }) {
                Text(
                    if (authState.isSubmitting) {
                        if (isSignUp) "Signing up..." else "Signing in..."
                    } else {
                        if (isSignUp) "Sign up" else "Sign in"
                    }
                )
            }

            // Consent line — governs BOTH Google and email sign-up (sign-in-wrap), so it sits under
            // everything rather than being pinned to one button.
            Div(attrs = {
                style {
                    fontSize(12.px); color(Gray500); property("text-align", "center")
                    property("line-height", "1.5"); marginTop(14.px)
                }
            }) {
                Text("By continuing, you agree to the ")
                A(href = "/terms", attrs = { attr("target", "_blank"); style { color(Primary); fontWeight(600) } }) { Text("Terms of Service") }
                Text(" and ")
                A(href = "/privacy", attrs = { attr("target", "_blank"); style { color(Primary); fontWeight(600) } }) { Text("Privacy Policy") }
                Text(".")
            }
        }
    }
}


/**
 * "Your first N analyses are completely free" / "You have N free analyses left" (see
 * `freeAnalysesNudge`) — sits where the Quick/Full toggle would be, for a visitor or a registered
 * account with NO subscription of any kind (`Entitlement.hasAnySubscription`) who still has free full
 * analyses. The caller decides whether to draw it (`freeAnalysesBannerText`, which is null when there is
 * nothing to promise: 0 free analyses, or a subscriber); [text] is what that returned.
 *
 * Deliberately a statement, not a control: the mode is pinned to FULL for these users, so there is
 * nothing to choose. Its job is to price the next click BEFORE they make it, so the paywall on
 * search #2 reads as "the free one is used up" rather than an ambush.
 *
 * A content-hugging pill (NOT the search bar's full width) that floats gently up and down, so it
 * reads as a badge on the search rather than another input-sized row.
 *
 * Mirrors the mobile FreeAnalysisNudge in MainScreen.kt — keep the two in step.
 */
@Composable
private fun FreeAnalysisNudge(text: String) {
    // Via the shared InjectStyles helper, NOT Compose HTML's `Style { }` builder — that idiom
    // silently emits an EMPTY <style> tag and the keyframes never exist. See StyleInjection.kt.
    InjectStyles(
        "ff-free-nudge-styles",
        """
        @keyframes ff-free-nudge-float {
            0%, 100% { transform: translateY(0); }
            50%      { transform: translateY(-6px); }
        }
        """.trimIndent()
    )
    // Centering wrapper: the pill itself is inline-flex so it hugs its text, and this row is what
    // keeps it centred under the search bar instead of pinned left.
    Div(attrs = {
        style {
            display(DisplayStyle.Flex)
            justifyContent(JustifyContent.Center)
            width(100.percent)
            marginTop(14.px)
        }
    }) {
        Div(attrs = {
            style {
                property("display", "inline-flex")
                alignItems(AlignItems.Center)
                property("gap", "8px")
                padding(6.px, 18.px)
                borderRadius(999.px)
                backgroundColor(PrimaryPale)
                property("border", "1px solid rgba(14, 124, 123, 0.25)")
                property("animation", "ff-free-nudge-float 2.4s ease-in-out infinite")
                // Promote to its own layer so the loop can't repaint the search bar above it.
                property("will-change", "transform")
            }
        }) {
            Div(attrs = { style { fontSize(18.px); property("line-height", "1") } }) { Text("🎁") }
            Div(attrs = {
                style { fontSize(14.px); fontWeight(700); color(Primary); property("white-space", "nowrap") }
            }) { Text(text) }
        }
    }
}
